window.theme = window.theme || {};

theme.config = {
  mqlSmall: false,
  mediaQuerySmall: 'screen and (max-width: 749px)',
  isTouch:
    'ontouchstart' in window ||
      (window.DocumentTouch && window.document instanceof DocumentTouch) ||
      window.navigator.maxTouchPoints ||
      window.navigator.msMaxTouchPoints
      ? true
      : false,
  rtl: document.documentElement.getAttribute('dir') === 'rtl' ? true : false,
};

/**
 * 读取 document.cookie（与 vernal-sensors 中逻辑一致）。
 * 必须定义在 global.js 内：vernal-sensors.js 仅在部分店铺加载且 getCookie 未暴露到全局。
 */
function getCookie(name) {
  if (typeof document === 'undefined' || !document.cookie) return null;
  const all = document.cookie.split('; ');
  for (let i = 0; i < all.length; i++) {
    const part = all[i];
    const idx = part.indexOf('=');
    if (idx === -1) continue;
    const k = decodeURIComponent(part.slice(0, idx));
    if (k === name) return decodeURIComponent(part.slice(idx + 1));
  }
  return null;
}

function deleteCookie(name) {
  const encoded = encodeURIComponent(name);
  document.cookie = `${encoded}=; Max-Age=0; Path=/; SameSite=Lax`;
  document.cookie = `${encoded}=; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Path=/; SameSite=Lax`;
}

function getVariantFeaturedImageSrc(variant) {
  if (!variant) return null;
  if (variant.featured_media?.preview_image?.src) {
    return variant.featured_media.preview_image.src;
  }
  if (variant.featured_image?.src) return variant.featured_image.src;
  if (typeof variant.featured_image === 'string') return variant.featured_image;
  return null;
}

function normalizeOptionValue(value) {
  return value == null ? '' : String(value).normalize('NFC').trim();
}

function normalizeOptionName(name) {
  if (name == null) return '';
  return String(name).trim().toLowerCase().replace(/\s+/g, '-');
}

function isColorOptionName(name) {
  const normalized = normalizeOptionName(name);
  if (!normalized) return false;
  return (
    /^(color|colour|farbe|couleur|colore|颜色)$/.test(normalized) ||
    normalized.includes('color') ||
    normalized.includes('colour') ||
    normalized.includes('farbe')
  );
}

function isTextureOptionName(name) {
  const normalized = normalizeOptionName(name);
  if (!normalized) return false;
  return (
    /^(material|texture|finish|surface|desktop|tabletop|tischplatte|platte|style|wood|top)$/.test(normalized) ||
    normalized.includes('material') ||
    normalized.includes('texture') ||
    normalized.includes('desktop') ||
    normalized.includes('tabletop') ||
    normalized.includes('finish') ||
    normalized.includes('surface') ||
    normalized.includes('tischplatte') ||
    normalized.includes('platte')
  );
}

function isFrameOptionName(name) {
  const normalized = normalizeOptionName(name);
  if (!normalized) return false;
  return (
    normalized === 'frame' ||
    normalized.includes('frame') ||
    normalized === 'gestell' ||
    normalized.includes('gestell')
  );
}

function isMainColorSyncOptionName(name) {
  return isColorOptionName(name) || isFrameOptionName(name);
}

function isAccessoryLocalOnlyOption(name) {
  const normalized = normalizeOptionName(name);
  if (!normalized) return false;
  return (
    normalized === 'size' ||
    normalized.includes('size') ||
    normalized.includes('größe') ||
    normalized.includes('groesse') ||
    normalized.includes('dimension') ||
    normalized.includes('style') ||
    normalized.includes('length') ||
    normalized.includes('width') ||
    normalized === 'type' ||
    normalized === 'typ'
  );
}

function getOptionSemanticType(name) {
  if (isColorOptionName(name)) return 'color';
  if (isTextureOptionName(name)) return 'texture';
  return 'other';
}

function normalizeOptionValueForMatch(value) {
  return normalizeOptionValue(value).replace(/_/g, ' ').replace(/\s+/g, ' ').toLowerCase();
}

function optionValuesEqual(left, right) {
  return normalizeOptionValueForMatch(left) === normalizeOptionValueForMatch(right);
}

const DESKTOP_MATERIAL_PREFIX_PATTERN =
  /^(laminate|hardwood|bamboo|solid wood|solidwood|spanplatte|massivholz|naturbambus|natur bambus|bambus|natural bamboo)(?:[\s\-_]+|$)/i;

function stripDesktopMaterialPrefix(value) {
  const normalized = normalizeOptionValueForMatch(value);
  return normalized.replace(DESKTOP_MATERIAL_PREFIX_PATTERN, '').trim();
}

function looksLikeDesktopMaterialValue(value) {
  const normalized = normalizeOptionValueForMatch(value);
  return DESKTOP_MATERIAL_PREFIX_PATTERN.test(normalized);
}

function desktopValuesMatch(accessoryValue, mainDesktopValue) {
  if (optionValuesEqual(accessoryValue, mainDesktopValue)) return true;

  const normAcc = normalizeOptionValueForMatch(accessoryValue);
  const normMain = normalizeOptionValueForMatch(mainDesktopValue);
  if (!normAcc || !normMain) return false;

  // 两侧都带材质前缀时（如 Laminate - Walnut vs Hardwood - Walnut），
  // 只比后缀会误判为同一规格，必须要求完整值相等（已在上方处理）。
  if (looksLikeDesktopMaterialValue(accessoryValue) && looksLikeDesktopMaterialValue(mainDesktopValue)) {
    return false;
  }

  const accSuffix = stripDesktopMaterialPrefix(accessoryValue);
  const mainSuffix = stripDesktopMaterialPrefix(mainDesktopValue);

  // 一侧是完整材质规格、另一侧是纯颜色名时，允许用后缀匹配（如 Hardwood - Walnut ↔ Walnut）
  if (!looksLikeDesktopMaterialValue(accessoryValue) && mainSuffix && normAcc === mainSuffix) return true;
  if (!looksLikeDesktopMaterialValue(mainDesktopValue) && accSuffix && normMain === accSuffix) return true;

  return false;
}

const DEFAULT_DESKTOP_ACCESSORY_COLOR_MAPS = {
  us: {
    'Laminate - Walnut': 'Walnut',
    'Laminate - Light Walnut': 'Light Walnut',
    'Laminate - White': 'Light Walnut',
    'Hardwood - Cherry': 'Light Walnut',
    'Hardwood - Maple': 'Light Walnut',
    'Hardwood - Walnut': 'Walnut',
  },
  uk: {
    'Laminate-Walnut': 'Walnut',
    'Laminate-Light Walnut': 'Light_Walnut',
    'Laminate-White': 'Light_Walnut',
    'Natural Bamboo': 'Light_Walnut',
  },
  de: {
    'Spanplatte - Walnussholz': 'Walnussholz',
    'Spanplatte - Helles Walnussholz': 'Helles Walnussholz',
    'Spanplatte - Weiß': 'Helles Walnussholz',
    Spanplatte_Weiß: 'Helles Walnussholz',
    Naturbambus: 'Helles Walnussholz',
    'Massivholz - Kirschholz': 'Helles Walnussholz',
    'Massivholz - Ahornholz': 'Helles Walnussholz',
    'Massivholz - Walnussholz': 'Walnussholz',
  },
};

function resolveShopKeyForDesktopColorMap() {
  return String(
    window.__vernal?.shopKey || window.shop_country || document.documentElement.dataset.shopKey || ''
  ).toLowerCase();
}

function getDesktopAccessoryColorMap() {
  const colorMapEl = document.querySelector('#desktop_accessory_tone_map');
  if (colorMapEl) {
    try {
      const parsed = JSON.parse(colorMapEl.textContent);
      if (parsed && typeof parsed === 'object' && Object.keys(parsed).length) {
        return parsed;
      }
    } catch {
      // fall through to shop defaults
    }
  }

  const shopKey = resolveShopKeyForDesktopColorMap();
  return DEFAULT_DESKTOP_ACCESSORY_COLOR_MAPS[shopKey] || null;
}

function getMainProductCurrentVariant() {
  if (window.currentVariant) return window.currentVariant;

  const picker =
    document.querySelector('variant-radios:not([data-tplsuffix="accessory-modal"])') ||
    document.querySelector('variant-selects:not([data-tplsuffix="accessory-modal"]):not([data-no-scroll])');

  return picker?.currentVariant || null;
}

function getMainDesktopAccessoryColorAlias(mainVariant) {
  if (!mainVariant?.option1) return null;

  const colorMap = getDesktopAccessoryColorMap();
  if (!colorMap) return null;

  const desktopValue = normalizeOptionValue(mainVariant.option1);
  if (colorMap[desktopValue] != null) {
    return normalizeOptionValue(colorMap[desktopValue]);
  }

  const normalizedDesktop = normalizeOptionValueForMatch(mainVariant.option1);
  for (const [desktopKey, aliasColor] of Object.entries(colorMap)) {
    if (normalizeOptionValueForMatch(desktopKey) === normalizedDesktop) {
      return normalizeOptionValue(aliasColor);
    }
  }

  return null;
}

function accessoryColorMatchesAlias(accessoryColorValue, aliasColor) {
  if (!aliasColor || accessoryColorValue == null) return false;
  if (optionValuesEqual(accessoryColorValue, aliasColor)) return true;
  return desktopValuesMatch(accessoryColorValue, aliasColor) || desktopValuesMatch(aliasColor, accessoryColorValue);
}

function isShortAccessoryColorPalette(productId, accessoryOptionNames) {
  const colorValues = [];

  accessoryOptionNames.forEach((optionName, index) => {
    if (isAccessoryLocalOnlyOption(optionName)) return;
    getAccessoryOptionValues(productId, index).forEach((value) => colorValues.push(value));
  });

  if (!colorValues.length) return false;
  return colorValues.every((value) => !looksLikeDesktopMaterialValue(value));
}

function variantMatchesDesktopColorAlias(variant, syncChecks, mainVariant) {
  const aliasColor = getMainDesktopAccessoryColorAlias(mainVariant);
  if (!aliasColor) return false;

  return syncChecks.every((check) => {
    const accessoryValue = variant[`option${check.optionIndex + 1}`];
    return accessoryColorMatchesAlias(accessoryValue, aliasColor);
  });
}

function pickAccessoryVariantByDesktopColorAlias(variants, params) {
  const { mainVariant, syncChecks, preferredVariantId, ignoreUnfit = false } = params;
  if (!syncChecks?.length || !getMainDesktopAccessoryColorAlias(mainVariant)) return null;

  const isCandidate = (variant) => isAccessoryVariantCandidate(variant, mainVariant.sku, { ignoreUnfit });
  const aliasMatches = variants.filter(
    (variant) => isCandidate(variant) && variantMatchesDesktopColorAlias(variant, syncChecks, mainVariant)
  );

  return pickPreferredFromMatches(aliasMatches, preferredVariantId);
}

function getAccessoryOptionValues(productId, optionIndex) {
  const variants = getAccessoryVariants(productId);
  const values = new Set();
  variants.forEach((variant) => {
    const value = variant[`option${optionIndex + 1}`];
    if (value != null && value !== '') values.add(normalizeOptionValue(value));
  });
  return [...values];
}

function getMainProductOptionValuesByIndex(optionIndex) {
  const optionsWithValues = getMainProductOptionsWithValues();
  if (!optionsWithValues?.[optionIndex]?.values) return [];
  return optionsWithValues[optionIndex].values.map((value) => normalizeOptionValue(value));
}

function countValueOverlap(leftValues, rightValues, matcher = optionValuesEqual) {
  return leftValues.filter((left) => rightValues.some((right) => matcher(left, right))).length;
}

function inferAccessoryOptionSyncType(optionName, productId, optionIndex) {
  if (isAccessoryLocalOnlyOption(optionName)) return null;

  const namedType = getOptionSemanticType(optionName);
  if (namedType === 'texture') return 'texture';

  const accessoryValues = getAccessoryOptionValues(productId, optionIndex);
  if (!accessoryValues.length) return namedType === 'color' ? 'color' : null;

  const mainDesktopValues = getMainProductOptionValuesByIndex(0);
  const mainFrameValues = getMainProductOptionValuesByIndex(1);
  const desktopOverlap = countValueOverlap(accessoryValues, mainDesktopValues, desktopValuesMatch);
  const frameOverlap = countValueOverlap(accessoryValues, mainFrameValues);
  const hasDesktopMaterialValues = accessoryValues.some(looksLikeDesktopMaterialValue);

  if (frameOverlap > 0) {
    return 'color';
  }

  const shortWoodPalette =
    accessoryValues.length > 0 && accessoryValues.every((value) => !looksLikeDesktopMaterialValue(value));

  if (shortWoodPalette && (namedType === 'color' || namedType === 'texture')) {
    return 'texture';
  }

  if (desktopOverlap > 0 || hasDesktopMaterialValues) {
    return 'texture';
  }

  if (namedType === 'color') {
    return 'color';
  }

  return null;
}

function parseReplaceSelectedColorMap(replaceSelectedColorStr) {
  const replaceObj = {};
  if (!replaceSelectedColorStr) return replaceObj;

  replaceSelectedColorStr.split(',').forEach((colorStr) => {
    const trimmed = colorStr.trim();
    const lastDash = trimmed.lastIndexOf('-');
    if (lastDash > 0) {
      replaceObj[trimmed.slice(0, lastDash).trim()] = trimmed.slice(lastDash + 1).trim();
    }
  });

  return replaceObj;
}

function getMainProductOptionsWithValues() {
  const picker =
    document.querySelector('variant-radios[data-ismainproduct="1"]') ||
    document.querySelector('variant-radios:not([data-tplsuffix="accessory-modal"])') ||
    document.querySelector('variant-selects:not([data-tplsuffix="accessory-modal"]):not([data-no-scroll])');
  const optionsEl = picker?.querySelector('#product_options') || document.querySelector('#product_options');
  if (!optionsEl) return null;

  try {
    return JSON.parse(optionsEl.textContent);
  } catch {
    return null;
  }
}

function buildVariantOptionMap(variant, optionNames) {
  const map = {};
  if (!variant || !Array.isArray(optionNames)) return map;

  optionNames.forEach((name, index) => {
    const optionKey = `option${index + 1}`;
    if (variant[optionKey] != null && name) {
      map[normalizeOptionName(name)] = normalizeOptionValue(variant[optionKey]);
    }
  });

  return map;
}

function buildMainVariantOptionMap(mainVariant) {
  const optionsWithValues = getMainProductOptionsWithValues();
  const map = {};
  if (!optionsWithValues || !mainVariant) return map;

  optionsWithValues.forEach((opt, index) => {
    const optionKey = `option${index + 1}`;
    if (mainVariant[optionKey] != null && opt?.name) {
      map[normalizeOptionName(opt.name)] = normalizeOptionValue(mainVariant[optionKey]);
    }
  });

  return map;
}

function getAccessoryVariants(productId) {
  const variantsEl = document.querySelector(`#accessory-variants-data-${productId}`);
  if (!variantsEl) return [];

  try {
    return JSON.parse(variantsEl.textContent);
  } catch {
    return [];
  }
}

function isAccessoryVariantUnfit(variantSku, mainVariantSku) {
  if (!window.unfit_accessories_data?.length || !mainVariantSku) return false;

  return window.unfit_accessories_data.some(
    (unfitAcc) =>
      unfitAcc?.desks_sku?.includes(mainVariantSku) && unfitAcc?.accessories_sku?.includes(variantSku)
  );
}

function colorValuesMatch(accessoryValue, mainValue, mainVariant, replaceObj) {
  if (optionValuesEqual(accessoryValue, mainValue)) return true;

  const mappedColor = replaceObj?.[mainVariant?.option1];
  return mappedColor != null && optionValuesEqual(accessoryValue, mappedColor);
}

function getAccessoryOptionNames(productId, explicitOptionNames) {
  if (Array.isArray(explicitOptionNames) && explicitOptionNames.length) {
    return explicitOptionNames;
  }

  const optionsEl = document.querySelector(`#accessory-options-data-${productId}`);
  if (optionsEl) {
    try {
      const parsed = JSON.parse(optionsEl.textContent);
      if (Array.isArray(parsed) && parsed.length) return parsed;
    } catch {
      // fall through to variant-based inference
    }
  }

  const variants = getAccessoryVariants(productId);
  const firstVariant = variants[0];
  if (!firstVariant) return [];

  const optionCount = firstVariant.option3 != null ? 3 : firstVariant.option2 != null ? 2 : 1;
  if (optionCount === 1) return ['Color'];
  if (optionCount === 2) return ['Color', 'Size'];
  return Array.from({ length: optionCount }, (_, index) => `Option ${index + 1}`);
}

function isAccessoryVariantAvailable(variant) {
  return variant?.available !== false;
}

function isAccessoryVariantCandidate(variant, mainVariantSku, options = {}) {
  if (!isAccessoryVariantAvailable(variant)) return false;
  if (options.ignoreUnfit) return true;
  return !isAccessoryVariantUnfit(variant.sku, mainVariantSku);
}

function buildAccessorySyncChecks(accessoryOptionNames, mainVariant, autoMatchVariantOption, productId) {
  const checks = [];
  const mainColorKey = autoMatchVariantOption === 'option1' ? 'option1' : 'option2';
  const mainColorValue = mainVariant[mainColorKey];
  const mainTextureValue = mainVariant.option1;

  accessoryOptionNames.forEach((accName, index) => {
    const syncType = inferAccessoryOptionSyncType(accName, productId, index);
    if (!syncType) return;

    if (syncType === 'color') {
      checks.push({ optionIndex: index, type: 'color', mainValue: mainColorValue });
    } else if (syncType === 'texture') {
      checks.push({ optionIndex: index, type: 'texture', mainValue: mainTextureValue });
    }
  });

  const hasColorCheck = checks.some((check) => check.type === 'color');

  if (!hasColorCheck && accessoryOptionNames.length >= 1 && !isAccessoryLocalOnlyOption(accessoryOptionNames[0])) {
    const firstSyncType = inferAccessoryOptionSyncType(accessoryOptionNames[0], productId, 0);
    if (!firstSyncType) {
      checks.push({
        optionIndex: 0,
        type: 'color',
        mainValue: mainColorValue,
        legacy: true,
      });
    }
  }

  return checks;
}

function variantMatchesSyncChecks(variant, checks, mainVariant, replaceObj) {
  if (!checks.length) return false;

  return checks.every((check) => {
    const accessoryValue = variant[`option${check.optionIndex + 1}`];
    if (accessoryValue == null || check.mainValue == null) return false;

    if (check.type === 'color') {
      return colorValuesMatch(accessoryValue, check.mainValue, mainVariant, replaceObj);
    }

    return desktopValuesMatch(accessoryValue, check.mainValue);
  });
}

function pickPreferredFromMatches(matches, preferredVariantId) {
  if (!matches.length) return null;
  if (preferredVariantId) {
    const preferred = matches.find((variant) => Number(variant.id) === Number(preferredVariantId));
    if (preferred) return preferred;
  }
  return matches[0];
}

function pickInStockAccessoryFallback(variants, params) {
  const { accessoryOptionNames, mainVariant, preferredVariantId, ignoreUnfit = false } = params;
  const available = variants.filter((variant) =>
    isAccessoryVariantCandidate(variant, mainVariant.sku, { ignoreUnfit })
  );
  if (!available.length) return null;

  const localOptionIndices = accessoryOptionNames
    .map((name, index) => (isAccessoryLocalOnlyOption(name) ? index : null))
    .filter((index) => index != null);

  if (preferredVariantId && localOptionIndices.length) {
    const preferred = variants.find((variant) => Number(variant.id) === Number(preferredVariantId));
    if (preferred) {
      const sameLocalMatches = available.filter((variant) =>
        localOptionIndices.every((index) =>
          optionValuesEqual(variant[`option${index + 1}`], preferred[`option${index + 1}`])
        )
      );
      const sameLocalPick = pickPreferredFromMatches(sameLocalMatches, preferredVariantId);
      if (sameLocalPick) return sameLocalPick;
    }
  }

  return available[0];
}

function pickAccessoryVariantMatch(variants, params) {
  const {
    mainVariant,
    accessoryOptionNames,
    autoMatchVariantOption,
    replaceObj,
    preferredVariantId,
    productId,
    ignoreUnfit = false,
  } = params;

  const checks = buildAccessorySyncChecks(accessoryOptionNames, mainVariant, autoMatchVariantOption, productId);
  if (!checks.length) return null;

  const isCandidate = (variant) => isAccessoryVariantCandidate(variant, mainVariant.sku, { ignoreUnfit });
  const matchesAllChecks = (variant) => variantMatchesSyncChecks(variant, checks, mainVariant, replaceObj);

  const allChecksMatches = variants.filter((variant) => isCandidate(variant) && matchesAllChecks(variant));
  const allChecksPick = pickPreferredFromMatches(allChecksMatches, preferredVariantId);
  if (allChecksPick) return allChecksPick;

  const colorChecks = checks.filter((check) => check.type === 'color');
  const textureChecks = checks.filter((check) => check.type === 'texture');

  if (colorChecks.length) {
    const colorMatches = variants.filter(
      (variant) =>
        isCandidate(variant) && variantMatchesSyncChecks(variant, colorChecks, mainVariant, replaceObj)
    );
    const colorPick = pickPreferredFromMatches(colorMatches, preferredVariantId);
    if (colorPick) return colorPick;
  }

  if (textureChecks.length) {
    const textureMatches = variants.filter(
      (variant) =>
        isCandidate(variant) && variantMatchesSyncChecks(variant, textureChecks, mainVariant, replaceObj)
    );
    const texturePick = pickPreferredFromMatches(textureMatches, preferredVariantId);
    if (texturePick) return texturePick;
  }

  const shortPalette = isShortAccessoryColorPalette(productId, accessoryOptionNames);
  const aliasSyncChecks = shortPalette ? [...textureChecks, ...colorChecks] : textureChecks;

  if (aliasSyncChecks.length && shortPalette) {
    const aliasPick = pickAccessoryVariantByDesktopColorAlias(variants, {
      mainVariant,
      syncChecks: aliasSyncChecks,
      preferredVariantId,
      ignoreUnfit,
    });
    if (aliasPick) return aliasPick;
  }

  return pickInStockAccessoryFallback(variants, {
    accessoryOptionNames,
    mainVariant,
    preferredVariantId,
    ignoreUnfit,
  });
}

function findMatchingAccessoryVariant(params) {
  const {
    mainVariant,
    productId,
    replaceSelectedColor = '',
    autoMatchVariantOption = 'option2',
    accessoryOptionNames: explicitOptionNames,
    preferredVariantId,
    ignoreUnfit = false,
  } = params;

  if (!mainVariant || !productId) return null;

  const variants = getAccessoryVariants(productId);
  if (!variants.length) return null;

  const accessoryOptionNames = getAccessoryOptionNames(productId, explicitOptionNames);
  const replaceObj = parseReplaceSelectedColorMap(replaceSelectedColor);

  return pickAccessoryVariantMatch(variants, {
    mainVariant,
    accessoryOptionNames,
    autoMatchVariantOption,
    replaceObj,
    preferredVariantId,
    productId,
    ignoreUnfit,
  });
}

function getAccessoryVariantImageUrl(productId, variantId) {
  const variantsData = document.querySelector(`#accessory-variants-data-${productId}`);
  if (variantsData) {
    try {
      const variants = JSON.parse(variantsData.textContent);
      const variant = variants.find((v) => Number(v.id) === Number(variantId));
      if (variant) return getVariantFeaturedImageSrc(variant);
    } catch (e) {
      // ignore
    }
  }
  if (window.currentVariant && Number(window.currentVariant.id) === Number(variantId)) {
    return getVariantFeaturedImageSrc(window.currentVariant);
  }
  return null;
}

function parseBundleProductIds(raw) {
  if (!raw || typeof raw !== 'string') return [];
  return raw
    .split(/[,\n\r]+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map(Number)
    .filter((id) => Number.isFinite(id) && id > 0);
}

function resolveMainProductId(fallbackId) {
  if (fallbackId) return Number(fallbackId);
  const purchaseEl = document.querySelector('[id^="main-product-purchase-"][data-product-id]');
  if (purchaseEl?.dataset?.productId) return Number(purchaseEl.dataset.productId);
  const form = document.querySelector(
    'variant-radios:not([data-tplsuffix="accessory-modal"]) product-form[data-id], product-form[data-id]'
  );
  if (form?.dataset?.id) return Number(form.dataset.id);
  return null;
}

function getAccessoriesBundleDiscountConfig() {
  if (window.UniEventBus && typeof window.UniEventBus.pullData === 'function') {
    const fromBus = window.UniEventBus.pullData('ACCESSORIES_BUNDLE_DISCOUNT_CONFIG');
    if (fromBus) return fromBus;
  }
  const el = document.querySelector('accessories-sales');
  if (!el) return null;
  return {
    bundleProductIds: parseBundleProductIds(el.dataset.bundleProductIds),
    discountType: el.dataset.bundleDiscountType || 'fixed_amount',
    fixedAmount: parseFloat(el.dataset.bundleDiscountFixedAmount) || 0,
    percentage: Number(el.dataset.bundleDiscountPercentage) || 0,
    showDiscountAmount: el.dataset.bundleShowDiscount === 'true',
    mainProductId: Number(el.dataset.mainProductId) || null,
  };
}

function calculateAccessoriesBundleDisplayPrice(params) {
  const { config, mainProductId, mainPriceCents, mainCompareAtCents, selectedAccessories } = params;
  const accessories = selectedAccessories || [];
  const accessoriesTotalCents = accessories.reduce((s, a) => s + Number(a.price) * 100, 0);
  const basePriceCents = mainPriceCents + accessoriesTotalCents;
  const baseCompareAtCents = mainCompareAtCents ? mainCompareAtCents + accessoriesTotalCents : 0;

  if (!config?.bundleProductIds?.length) {
    return {
      priceCents: basePriceCents,
      compareAtCents: mainCompareAtCents ? baseCompareAtCents : null,
      bundleActive: false,
      savingsCents: 0,
    };
  }

  const bundleIds = config.bundleProductIds;
  const resolvedMainProductId = resolveMainProductId(mainProductId || config.mainProductId);
  const selectedIds = new Set(
    [resolvedMainProductId, ...accessories.map((a) => Number(a.productId))].filter(Boolean)
  );
  const bundleIdSet = new Set(bundleIds.map(Number));
  const allBundleSelected = bundleIds.every((id) => selectedIds.has(Number(id)));

  if (!allBundleSelected) {
    return {
      priceCents: basePriceCents,
      compareAtCents: mainCompareAtCents ? baseCompareAtCents : null,
      bundleActive: false,
      savingsCents: 0,
    };
  }

  let bundleSubtotalCents = 0;
  if (resolvedMainProductId && bundleIdSet.has(resolvedMainProductId)) {
    bundleSubtotalCents += mainPriceCents;
  }
  accessories.forEach((a) => {
    if (bundleIdSet.has(Number(a.productId))) {
      bundleSubtotalCents += Number(a.price) * 100;
    }
  });

  let nonBundleSubtotalCents = 0;
  accessories.forEach((a) => {
    if (!bundleIdSet.has(Number(a.productId))) {
      nonBundleSubtotalCents += Number(a.price) * 100;
    }
  });

  let discountCents = 0;
  if (config.discountType === 'percentage') {
    const pct = Number(config.percentage) || 0;
    if (pct > 0) {
      discountCents = Math.round((bundleSubtotalCents * pct) / 100);
    }
  } else {
    const fixedCents = Math.round((parseFloat(config.fixedAmount) || 0) * 100);
    if (fixedCents > 0) {
      discountCents = Math.min(fixedCents, bundleSubtotalCents);
    }
  }

  if (discountCents <= 0) {
    return {
      priceCents: basePriceCents,
      compareAtCents: mainCompareAtCents ? baseCompareAtCents : null,
      bundleActive: false,
      savingsCents: 0,
    };
  }

  const priceCents = bundleSubtotalCents - discountCents + nonBundleSubtotalCents;
  const compareAtCents = config.showDiscountAmount ? bundleSubtotalCents + nonBundleSubtotalCents : null;

  return {
    priceCents,
    compareAtCents,
    bundleActive: true,
    savingsCents: discountCents,
  };
}

function buildCombinedProductDisplayPrice(mainPriceCents, mainCompareAtCents, selectedAccessories, mainProductId) {
  const config = getAccessoriesBundleDiscountConfig();
  const result = calculateAccessoriesBundleDisplayPrice({
    config,
    mainProductId: resolveMainProductId(mainProductId || config?.mainProductId),
    mainPriceCents,
    mainCompareAtCents: mainCompareAtCents || 0,
    selectedAccessories: selectedAccessories || [],
  });
  return {
    ...result,
    formattedPrice: theme.Currency.formatMoney(result.priceCents, shopSettings.moneyFormat),
    formattedComparePrice: result.compareAtCents
      ? theme.Currency.formatMoney(result.compareAtCents, shopSettings.moneyFormat)
      : null,
  };
}

function formatAddToCartButtonText(mainPriceCents, accessories, mainCompareAtCents) {
  const count = 1 + (accessories?.length || 0);
  const display = buildCombinedProductDisplayPrice(mainPriceCents, mainCompareAtCents, accessories);
  const priceStr = display.formattedPrice;
  if (window.accessoryStrings) {
    const tpl = count === 1 ? window.accessoryStrings.addItemToCart : window.accessoryStrings.addItemsToCart;
    if (tpl && !tpl.includes('Translation missing')) {
      return tpl.replace('[count]', count).replace('[price]', priceStr);
    }
  }
  const itemLabel = count === 1 ? 'item' : 'items';
  return `Add ${count} ${itemLabel} to cart (${priceStr})`;
}

theme.Currency = (function () {
  const moneyFormat = '${{amount}}'; // eslint-disable-line camelcase

  function formatMoney(cents, format) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || moneyFormat;

    function formatWithDelimiters(number, precision, thousands, decimal) {
      thousands = thousands || ',';
      decimal = decimal || '.';

      if (isNaN(number) || number === null) {
        return 0;
      }

      number = (number / 100.0).toFixed(precision);

      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsAmount = parts[1] ? decimal + parts[1] : '';

      return dollarsAmount + centsAmount;
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'");
        break;
    }

    return formatString.replace(placeholderRegex, value);
  }

  return {
    formatMoney: formatMoney,
  };
})();

// Init section function when it's visible, then disable observer
theme.initWhenVisible = function (options) {
  const threshold = options.threshold ? options.threshold : 0;

  const observer = new IntersectionObserver(
    (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (typeof options.callback === 'function') {
            options.callback();
            observer.unobserve(entry.target);
          }
        }
      });
    },
    { rootMargin: `0px 0px ${threshold}px 0px` }
  );

  observer.observe(options.element);
};

(function () {
  // Trigger events when going between breakpoints
  const mql = window.matchMedia(theme.config.mediaQuerySmall);
  theme.config.mqlSmall = mql.matches;
  mql.onchange = (e) => {
    if (e.matches) {
      theme.config.mqlSmall = true;
      document.dispatchEvent(new CustomEvent('matchSmall'));
    } else {
      theme.config.mqlSmall = false;
      document.dispatchEvent(new CustomEvent('unmatchSmall'));
    }
  };

  // Page transition
  window.addEventListener('beforeunload', () => {
    document.body.classList.add('unloading');
  });
  window.addEventListener('DOMContentLoaded', () => {
    document.body.classList.add('loaded');
  });
  window.addEventListener('pageshow', (event) => {
    // Removes unload class when returning to page via history
    if (event.persisted) {
      document.body.classList.remove('unloading');
    }
  });

  // Viewport height：全端写入可视高度，避免 100vh 含浏览器工具栏导致侧栏底部被裁切
  function viewportHeight() {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    scheduleNavCompactModeUpdate();
    setHeaderHeight();
    setAnnouncementBarHeight();
  }
  window.addEventListener('resize', viewportHeight);
  window.addEventListener('DOMContentLoaded', () => {
    viewportHeight();
    initAnnouncementBarHeightObserver();
    initNavCompactModeObserver();
  });
  window.addEventListener('load', scheduleNavCompactModeUpdate);

  if (document.body.getAttribute('data-page-rendering') !== null) {
    // Improve initial load time by skipping the rendering of offscreen content
    const observer = new IntersectionObserver(
      (entries, _observer) => {
        entries.forEach((entry) => {
          const el = entry.target;
          // Not currently in intersection area.
          if (entry.intersectionRatio == 0) {
            return;
          }
          // Trigger rendering for elements within scroll area that haven't already been marked.
          if (!el.markedVisible) {
            el.attributeStyleMap && el.attributeStyleMap.set('content-visibility', 'visible');
            el.markedVisible = true;
          }
        });
      },
      // Set a rendering 50px above and 100px below the main scroll area.
      { rootMargin: '50px 0px 100px 0px' }
    );
    const sections = document.querySelectorAll('.shopify-section + .shopify-section');
    sections.forEach((section) => {
      observer.observe(section);
    });
  }

  /*
  // Delay JavaScript until user interaction
  const loadScriptsTimer = setTimeout(loadScripts, 5000);
  const userInteractionEvents = ["mouseover", "mousemove", "keydown", "touchstart", "touchend", "touchmove", "wheel"];
  userInteractionEvents.forEach(function(event) {
    window.addEventListener(event, triggerScriptLoader, {
      passive: !0
    });
  });
  function triggerScriptLoader() {
    loadScripts();
    clearTimeout(loadScriptsTimer);
    userInteractionEvents.forEach(function(event) {
      window.removeEventListener(event, triggerScriptLoader, {
        passive: !0
      });
    });
  }
  function loadScripts() {
    document.querySelectorAll("script[data-type='lazy']").forEach(function(script) {
      script.setAttribute("src", script.getAttribute("data-src"));
    });
  }
  */
})();

// Prevent vertical scroll while using flickity sliders
(function () {
  var e = !1;
  var t;

  document.body.addEventListener('touchstart', function (i) {
    if (!i.target.closest('.flickity-slider')) {
      return (e = !1);
    }
    e = !0;
    t = {
      x: i.touches[0].pageX,
      y: i.touches[0].pageY,
    };
  });

  document.body.addEventListener(
    'touchmove',
    function (i) {
      if (e && i.cancelable) {
        var n = {
          x: i.touches[0].pageX - t.x,
          y: i.touches[0].pageY - t.y,
        };
        Math.abs(n.x) > Flickity.defaults.dragThreshold && i.preventDefault();
      }
    },
    { passive: !1 }
  );
})();

function isStorageSupported(type) {
  // Return false if we are in an iframe without access to sessionStorage
  if (window.self !== window.top) {
    return false;
  }

  const testKey = 'beyours:test';
  let storage;
  if (type === 'session') {
    storage = window.sessionStorage;
  }
  if (type === 'local') {
    storage = window.localStorage;
  }

  try {
    storage.setItem(testKey, '1');
    storage.removeItem(testKey);
    return true;
  } catch (error) {
    // Do nothing, this may happen in Safari in incognito mode
    return false;
  }
}

function checkScrollbar() {
  const rect = document.body.getBoundingClientRect();
  return Math.round(rect.left + rect.right) < window.innerWidth;
}

function setScrollbarWidth() {
  if (checkScrollbar()) {
    const element = document.createElement('span');
    (element.className = 'modal-scrollbar-measure'), document.body.appendChild(element);

    const width = element.getBoundingClientRect().width - element.clientWidth;
    document.body.removeChild(element);
    document.documentElement.style.setProperty('--scrollbar-width', `${width}px`);
  }
}

function setHeaderHeight() {
  const header = document.getElementById('shopify-section-header');
  if (!header) return;

  const rect = header.getBoundingClientRect();
  document.documentElement.style.setProperty('--header-height', `${Math.round(rect.height)}px`);
  // 距视口顶部到 header 底边的距离（含上方公告栏是否仍可见），供侧栏/抽屉 height 使用
  document.documentElement.style.setProperty(
    '--header-offset-bottom',
    `${Math.max(0, Math.round(rect.bottom))}px`
  );
}

function remToPx(rem) {
  return rem * parseFloat(getComputedStyle(document.documentElement).fontSize);
}

function getNormalNavColumnGapPx() {
  // 与 component-mega-menu-v2 / header 内联样式保持一致：≤1400 已默认 compact(1.6rem)
  if (window.matchMedia('(max-width: 1400px)').matches) return remToPx(1.6);
  if (window.matchMedia('(max-width: 1500px)').matches) return remToPx(2.2);
  return remToPx(2.8);
}

function getNavItemsRowWidth(items, gapPx) {
  let width = 0;
  items.forEach((item, index) => {
    if (index > 0) width += gapPx;
    width += item.getBoundingClientRect().width;
  });
  return width;
}

let navCompactRaf = null;

function scheduleNavCompactModeUpdate() {
  if (navCompactRaf !== null) return;
  navCompactRaf = requestAnimationFrame(() => {
    navCompactRaf = null;
    updateNavCompactMode();
  });
}

function updateNavCompactMode() {
  const header = document.querySelector('.header--new-menu');
  const list = header?.querySelector('.list-menu--inline');
  if (!header || !list) return;

  // 移动端抽屉 / ≤1400 已由 CSS 直接使用 compact 间距，无需再靠 class 切换避免 CLS
  if (window.matchMedia('(max-width: 1400px)').matches) {
    if (header.classList.contains('header--nav-compact')) {
      header.classList.remove('header--nav-compact');
    }
    setHeaderHeight();
    return;
  }

  const items = list.querySelectorAll(':scope > li');
  if (items.length < 2) {
    if (header.classList.contains('header--nav-compact')) {
      header.classList.remove('header--nav-compact');
      setHeaderHeight();
    }
    return;
  }

  const firstTop = items[0].offsetTop;
  const isWrapped = Array.from(items).some(
    (item, index) => index > 0 && item.offsetTop > firstTop + 1
  );

  const normalGap = getNormalNavColumnGapPx();
  const itemsWidth = getNavItemsRowWidth(items, normalGap);
  const isCompact = header.classList.contains('header--nav-compact');
  const hysteresisPx = 24;

  let shouldBeCompact;
  if (isCompact) {
    shouldBeCompact = isWrapped || itemsWidth > list.clientWidth - hysteresisPx;
  } else {
    shouldBeCompact = isWrapped || itemsWidth > list.clientWidth + 1;
  }

  if (isCompact !== shouldBeCompact) {
    header.classList.toggle('header--nav-compact', shouldBeCompact);
    setHeaderHeight();
  }
}

function initNavCompactModeObserver() {
  const inlineMenu = document.querySelector('.header--new-menu .header__inline-menu');
  if (!inlineMenu) return;

  scheduleNavCompactModeUpdate();

  if (typeof ResizeObserver === 'undefined') return;

  new ResizeObserver(() => scheduleNavCompactModeUpdate()).observe(inlineMenu);
}

function setAnnouncementBarHeight() {
  const announcementBar = document.getElementById('shopify-section-announcement-bar');
  const height = announcementBar ? Math.round(announcementBar.getBoundingClientRect().height) : 0;
  document.documentElement.style.setProperty('--announcement-bar-height', `${height}px`);
  // 公告栏高度变化会改变 header 相对视口的 bottom，需同步侧栏可用高度
  setHeaderHeight();
}

function initAnnouncementBarHeightObserver() {
  const announcementBar = document.getElementById('shopify-section-announcement-bar');
  if (!announcementBar || typeof ResizeObserver === 'undefined') return;

  new ResizeObserver(() => setAnnouncementBarHeight()).observe(announcementBar);
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    const charCode = event.code ? event.code.toUpperCase() : event.key.toUpperCase();
    if (charCode !== 'TAB') return; // If not TAB key

    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  if (elementToFocus) elementToFocus.focus();
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  const charCode = event.code ? event.code.toUpperCase() : event.key.toUpperCase();
  if (charCode !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

function filterShopifyEvent(event, domElement, callback) {
  let executeCallback = false;
  if (event.type.includes('shopify:section')) {
    if (domElement.hasAttribute('data-section-id') && domElement.getAttribute('data-section-id') === event.detail.sectionId) {
      executeCallback = true;
    }
  } else if (event.type.includes('shopify:block') && event.target === domElement) {
    executeCallback = true;
  }
  if (executeCallback) {
    callback(event);
  }
}

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

const serializeForm = (form) => {
  const obj = {};
  const formData = new FormData(form);

  for (const key of formData.keys()) {
    const regex = /(?:^(properties\[))(.*?)(?:\]$)/;

    if (regex.test(key)) {
      obj.properties = obj.properties || {};
      obj.properties[regex.exec(key)[2]] = formData.get(key);
    } else {
      obj[key] = formData.get(key);
    }
  }

  return JSON.stringify(obj);
};

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener ? target.addEventListener(eventName, callback, false) : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  const method = options['method'] || 'post';
  const params = options['parameters'] || {};

  const form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (const key in params) {
    const hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.postLink2 = function (path, options) {
  options = options || {};
  const method = options['method'] || 'post';
  const params = options['parameters'] || {};

  const form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (const key in params) {
    for (const index in params[key]) {
      for (const key2 in params[key][index]) {
        const hiddenField = document.createElement('input');
        hiddenField.setAttribute('type', 'hidden');
        hiddenField.setAttribute('name', `${key}[${index}][${key2}]`);
        hiddenField.setAttribute('value', params[key][index][key2]);
        form.appendChild(hiddenField);
      }
    }
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class QuantityInput extends HTMLElement {
  constructor() {
    super();

    this.init();
  }

  init() {
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });

    this.querySelectorAll('button').forEach((button) => button.addEventListener('click', this.onButtonClick.bind(this)));
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    event.target.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
  }
}
customElements.define('quantity-input', QuantityInput);

class MenuDropdown extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      isActive: 'is-active',
    };

    this.addEventListener('focusin', this.openDropdown.bind(this));
    this.addEventListener('focusout', this.closeDropdown.bind(this));
    this.addEventListener('mouseover', this.openDropdown.bind(this));
    this.addEventListener('mouseleave', this.closeDropdown.bind(this));
  }

  openDropdown() {
    this.classList.add(this.classes.isActive);

    //这里是为了给整个header加一个灰色背景蒙层
    // document.querySelector('.header-mask').classList.add(this.classes.isActive)
  }

  closeDropdown() {
    this.classList.remove(this.classes.isActive);
    // document.querySelector('.header-mask').classList.remove(this.classes.isActive)
  }
}
customElements.define('menu-dropdown', MenuDropdown);

class MenuDrawer extends HTMLElement {
  constructor(props) {
    super();

    this.classes = props
      ? props.classes
      : {
        open: 'menu-drawer--open',
        opening: 'menu-drawer--opening',
        closing: 'menu-drawer--closing',
      };

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) => summary.addEventListener('click', this.onSummaryClick.bind(this)));
    this.querySelectorAll('button').forEach((button) => {
      if (button.closest('header-new-drawer')) return;
      button.addEventListener('click', this.onCloseButtonClick.bind(this));
    });
  }

  onKeyUp(event) {
    const charCode = event.code ? event.code.toUpperCase() : event.key.toUpperCase();
    if (charCode !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sticky_header = document.querySelector('sticky-header');
    const miniCartBundleItem = document.querySelectorAll('mini-cart-bundle-item');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
    // 从外部调用sticky-header组件在移动端切换图片的方法
    sticky_header.switchHeaderBarStyle();

    // 从外部调用mini-cart-bundle-item组件打开抽屉的方法
    if (miniCartBundleItem) {
      miniCartBundleItem?.forEach((item) => item.openDrawer());
    }
  }

  openMenuDrawer(summaryElement) {
    setHeaderHeight();
    setAnnouncementBarHeight();
    setScrollbarWidth();
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');

      if (!this.cartItemsElement) this.cartItemsElement = document.querySelector('cart-items');
      if (this.cartItemsElement) {
        this.cartItemsElement.updateUnfitTips();
      }
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);

    setTimeout(() => {
      this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
      document.body.addEventListener('click', this.onBodyClickEvent);
    });
    document.body.classList.add(this.classes.opening);

    this.openAnimation();
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event !== undefined) {
      this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
        details.removeAttribute('open');
        details.classList.remove('menu-opening');
      });
      this.mainDetailsToggle.classList.remove('menu-opening');
      this.mainDetailsToggle.classList.add('menu-closing');
      removeTrapFocus(elementToFocus);

      document.body.removeEventListener('click', this.onBodyClickEvent);
      document.body.classList.remove(this.classes.open);
      document.querySelector('html').classList.remove(this.classes.open);
      document.body.classList.add(this.classes.closing);

      this.closeAnimation(this.mainDetailsToggle);
      this.dispatchEvent(new CustomEvent('close'));
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement)) this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus();
    this.closeAnimation(detailsElement);
  }

  openAnimation() {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        document.body.classList.remove(this.classes.opening);
        document.body.classList.add(this.classes.open);
        document.querySelector('html').classList.add(this.classes.open);
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        if (detailsElement === this.mainDetailsToggle) {
          document.body.classList.remove(this.classes.closing);
          detailsElement.classList.remove('menu-closing');
        }
        detailsElement.removeAttribute('open');

        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }

  onBodyClick(event) {
    if (
      event.composedPath().some(
        (el) => el instanceof Element && (el.matches('b-modal[open]') || el.matches('b-overlay[open]'))
      )
    ) {
      return;
    }

    // 对shop the look做特殊处理
    if (
      event.target.closest('.shop-the-look-left')
        ? !this.contains(event.target) && this.classList.contains('shop-the-look-left')
        : !this.contains(event.target)
    )
      this.closeMenuDrawer(event);
  }
}
customElements.define('menu-drawer', MenuDrawer);

class CartDrawer extends MenuDrawer {
  constructor() {
    super({
      classes: {
        open: 'mini-cart--open',
        opening: 'mini-cart--opening',
        closing: 'mini-cart--closing',
      },
    });
  }

  openMenuDrawer(summaryElement) {
    super.openMenuDrawer(summaryElement);
    // 打开后校正空态 / 组合折扣展示（样式已在 header 同步加载，不再拦截首次打开）
    const miniCart = document.querySelector('mini-cart');
    if (miniCart && typeof miniCart.syncEmptyState === 'function') {
      miniCart.syncEmptyState();
    }
    if (window.CartBundleDiscount && typeof window.CartBundleDiscount.refresh === 'function') {
      window.CartBundleDiscount.refresh({ skipApply: true }).catch(() => {});
    }
  }
}
customElements.define('cart-drawer', CartDrawer);

class FacetDrawer extends MenuDrawer {
  constructor() {
    super({
      classes: {
        open: 'facet-drawer--open',
        opening: 'facet-drawer--opening',
        closing: 'facet-drawer--closing',
      },
    });
  }
}
customElements.define('facet-drawer', FacetDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super({
      classes: {
        open: 'menu-mobile--open',
        opening: 'menu-mobile--opening',
        closing: 'menu-mobile--closing',
      },
    });
  }
}
customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this));
    this.addEventListener('keyup', (event) => {
      const charCode = event.code ? event.code.toUpperCase() : event.key.toUpperCase();
      if (charCode === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target.nodeName === 'MODAL-DIALOG') this.hide();
      });
    }
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    this.controlVideo(true);
    // window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    this.controlVideo(false);
    this.dispatchEvent(new CustomEvent('close'));
    // window.pauseAllMedia();
  }
  // 当对话框内包含video时
  controlVideo(isShow) {
    const innerVideo = this.querySelector('video');
    if (!innerVideo) return;
    isShow ? innerVideo.play() : innerVideo.pause();
  }
}
customElements.define('modal-dialog', ModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    this.button = this.querySelector('button') || this.querySelector('.opener-button');
    if (!this.button) return;

    this.button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) {
        modal.show(this.button);
        const videoNode = modal.querySelector('video');
        videoNode.play();
      }
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();

    this.poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!this.poster) return;

    this.poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent() {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      if (content.querySelector('video-section')) {
        this.appendChild(content).focus();
      } else {
        this.setAttribute('loaded', true);
        this.appendChild(content.querySelector('video, model-viewer, iframe')).focus();
      }
    }
    this.querySelector('video').play();
    this.querySelector('video').muted = false;
  }
}
customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();

    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    // 是否在搭售弹窗内的第一个sku
    this.inAcModalFirstVariant = false;

    if (!this.slider || !this.nextButton) return;

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 200,
    });
  }

  init() {
    const resizeObserver = new ResizeObserver(() => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(this.slider.clientWidth / this.sliderItemOffset);
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    // 目的是为了触发update中判断是否与previousPage不相等的条件
    this.currentPage = 0;
    this.initPages();
  }

  update() {
    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / Math.abs(this.sliderItemOffset)) + 1 || 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
      const spotsList = this.querySelectorAll('.spots-list .spot-item.multi-show');

      for (let i = 0; i < spotsList.length; i++) {
        // 如果是在搭售弹窗内，不展示第一张图片 这个功能不要先
        // const matchNum = this.inAcModalFirstVariant ? i + 1 : i;
        const matchNum = i;

        if (i + 1 !== this.currentPage) {
          spotsList[matchNum]?.classList.remove('spots-active');
        } else {
          spotsList[matchNum]?.classList.add('spots-active');
        }
      }
    }

    if (this.currentPage === 1 && this.currentPage === this.totalPages) {
      this.classList.add('slider--no-buttons');
    } else {
      this.classList.remove('slider--no-buttons');
    }

    if (this.enableSliderLooping) return;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (isFirstSlide || this.isSlideVisible(this.sliderItemsToShow[0])) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (isLastSlide || this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    debugger;
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.slideScrollPosition = theme.config.rtl ? this.slideScrollPosition * -1 : this.slideScrollPosition;
    this.slider.scrollTo({
      left: this.slideScrollPosition,
    });
  }
}
customElements.define('slider-component', SliderComponent);

class ThumbnailSlider extends SliderComponent {
  constructor() {
    super();
  }

  updateMediaCount() {
    if (!this.slider) return;

    const count = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0).length;
    this.slider.dataset.mediaCount = count;

    if (count < 2) this.classList.add('hidden');
    else this.classList.remove('hidden');
  }

  init() {
    super.init();
    this.updateMediaCount();
  }
}
customElements.define('thumbnail-slider', ThumbnailSlider);

class QuoteSlider extends SliderComponent {
  constructor() {
    super();
  }

  update() {
    super.update();

    if (this.currentPageElement && this.pageTotalElement) {
      this.sliderItems.forEach((element) => {
        element.removeAttribute('aria-current');
      });

      const currentElement = this.sliderItemsToShow[this.currentPage - 1];
      currentElement.setAttribute('aria-current', true);
    }
  }
}
customElements.define('quote-slider', QuoteSlider);

class ProductGallery extends SliderComponent {
  constructor() {
    super();
  }

  init() {
    super.init();

    const gallerySliderButtons = this.querySelector('.slider-buttons');
    if (gallerySliderButtons && this.querySelector('[data-gang-option]')) {
      if (this.querySelectorAll('.product__media-item.gang__active').length < 2) {
        gallerySliderButtons.classList.add('hidden');
      } else {
        gallerySliderButtons.classList.remove('hidden');
      }
    }

    this.cursor = this.querySelector('.gallery-cursor');
    if (!this.cursor) return;

    const images = this.querySelectorAll('.product__modal-opener--image');
    images.forEach((image) => image.addEventListener('mousemove', this.onMoveHandler.bind(this)));
    images.forEach((image) => image.addEventListener('mouseenter', this.onEnterHandler.bind(this)));
    images.forEach((image) => image.addEventListener('mouseleave', this.onLeaveHandler.bind(this)));
  }

  onMoveHandler(event) {
    this.cursor.style.left = `${event.clientX - 32}px`;
    this.cursor.style.top = `${event.clientY - 32}px`;
  }

  onEnterHandler() {
    this.cursor.classList.add('show');
  }

  onLeaveHandler() {
    this.cursor.classList.remove('show');
  }

  update() {
    super.update();

    if (this.currentPageElement && this.pageTotalElement) {
      const currentElement = this.sliderItemsToShow[this.currentPage - 1];
      if (currentElement) {
        const mediaEl = currentElement.querySelector('.media');
        if (mediaEl) {
          this.slider.style.setProperty(
            '--force-image-ratio-percent',
            mediaEl.style.getPropertyValue('--image-ratio-percent')
          );
        }
      }
    }
  }

  goToFirstSlide() {
    this.slider.scrollTo({
      left: 0,
    });
  }
}
customElements.define('product-gallery', ProductGallery);

function findVariantBySelectedOptions(variants, options) {
  if (!Array.isArray(variants) || !Array.isArray(options)) return undefined;

  return variants.find(
    (variant) =>
      Array.isArray(variant.options) &&
      variant.options.length === options.length &&
      variant.options.every((option, index) => normalizeOptionValue(option) === normalizeOptionValue(options[index]))
  );
}

function getMainProductVariantPicker(sectionId) {
  if (sectionId) {
    return (
      document.querySelector(`variant-radios[data-section="${sectionId}"][data-ismainproduct="1"]`) ||
      document.querySelector(`variant-radios[data-section="${sectionId}"]`) ||
      document.querySelector(`variant-selects[data-section="${sectionId}"]:not([data-no-scroll])`)
    );
  }

  return (
    document.querySelector('variant-radios[data-ismainproduct="1"]') ||
    document.querySelector('variant-radios[data-section]') ||
    document.querySelector('variant-selects[data-section]:not([data-no-scroll])')
  );
}

function resolveCurrentVariantSku(sectionId) {
  const picker = getMainProductVariantPicker(sectionId);
  const resolvedSectionId = sectionId || picker?.dataset?.section;

  if (resolvedSectionId) {
    const variantId = document.querySelector(`#product-form-${resolvedSectionId} input[name="id"]`)?.value;
    if (variantId && picker) {
      const variant = picker.getVariantData().find((item) => String(item.id) === String(variantId));
      if (variant?.sku) return variant.sku;
    }
  }

  if (picker?.currentVariant?.sku) return picker.currentVariant.sku;

  if (picker) {
    const variant = findVariantBySelectedOptions(picker.getVariantData(), picker.updateOptions());
    if (variant?.sku) return variant.sku;
  }

  if (resolvedSectionId) {
    const skuEl = document
      .getElementById(`price-${resolvedSectionId}`)
      ?.querySelector(`#sku-from-price-config-${resolvedSectionId}`);
    if (skuEl) return skuEl.getAttribute('data-targetSku') || '';
  }

  return window.currentVariantSku || '';
}

class VariantSelects extends HTMLElement {
  shouldSyncGlobalVariantState() {
    return this.dataset.tplsuffix !== 'accessory-modal' && !this.hasAttribute('data-no-scroll');
  }

  constructor() {
    super();

    this.addEventListener('change', this.onVariantChange);

    this.initColorLevel();
    this.initPromotionVariants();
    this.definePropertyCurrentVariantSkuChange();
    this.updateOptions();
    this.updateMasterId();
    // feat: 预售需求
    this.preOrderConfig = JSON.parse(this.querySelector('#pre_order_variants')?.textContent.trim() || '[]');
    this.preOrderBannerEl = this.querySelector('.pre-order__bar');
    this.updatePreOrderStatus();
    //
    this.dailyActivityConfig = JSON.parse(this.querySelector('#daily_activity_config')?.textContent.trim() || '{}');
    this.dailyActivityBannerEl = document.querySelector('.product__activity-bar');
    this.updateDailyActivityStatus();
    this.updateDeleteOptions();
    this.onlyLeg();
    this.lShaped();
    this.updatePriceDiff();
    this.updateModalPrice();
    this.updateOutOfStockNoticeStatus();
    this.updateFromPriceDiff();
    this.updateSizeDiagrams();
    this.initSplitOption();

    // mobile || accessory-modal
    if (document.body.clientWidth < 750 || this.dataset.tplsuffix == 'accessory-modal') {
      // document.querySelectorAll('.spots-list .spot-item.multi-show')[0].classList.remove('spots-active');
      // document.querySelectorAll('.spots-list .spot-item.multi-show')[1].classList.add('spots-active');

      // const mediaMultiShowItems = document.querySelectorAll('.product__media-list li.multi-show');
      // const mediaList = document.querySelector('.product__media-list');
      // const spotsList = document.querySelectorAll('.spots-list .spot-item.multi-show');

      // document.querySelector('.product__media-list').prepend(document.querySelectorAll('.product__media-list li.multi-show')[1]);
      // document.querySelector('.thumbnail-list').prepend(document.querySelectorAll('.thumbnail-list li.multi-show')[1]);
      // document.querySelector('.spots-list').prepend(document.querySelectorAll('.spots-list .spot-item.multi-show')[1]);

      document.querySelector('product-gallery').goToFirstSlide();
    }

    // this.ga4Report();
    // 配件区域的js文件是defer的，这里的通知时间延迟再触发以避免配件区域的js文件未加载完成
    setTimeout(() => {
      // 配件弹窗的选购区不触发
      if (this.dataset.tplsuffix !== 'accessory-modal') window.UniEventBus.submit('UPDATE_FIT_STATUS', this.currentVariant);
    }, 500);

    if (this.currentVariant) {
      // 如果当前有选中的variant，初始化一下产品信息 判断意思是否预售
      this.renderProductInfo();
    }

    if (this.dataset.tplsuffix !== 'accessory-modal') {
      const accessoryModal = document.querySelector('accessories-sales modal-dialog');
      if (accessoryModal) {
        accessoryModal.addEventListener('close', () => {
          this.updateOptions();
          this.updateMasterId();
        });
      }

      // 按顺序按下s,k,u，就把当前选中规格的 SKU 复制到剪贴板
      if (!window._skuCopyKeydownRegistered) {
        window._skuCopyKeydownRegistered = true;
        let keySequence = '';
        document.addEventListener('keydown', (event) => {
          keySequence += event.key.toLowerCase();
          if ('sku'.includes(keySequence)) {
            if (keySequence === 'sku') {
              const picker = getMainProductVariantPicker();
              const currentVariantSku = resolveCurrentVariantSku(picker?.dataset?.section);

              if (currentVariantSku) {
                navigator.clipboard.writeText(currentVariantSku);
                BMessage.success({ content: `SKU ${currentVariantSku} Copied!`, duration: 10 });
              }
              keySequence = '';
            }
          } else {
            keySequence = '';
          }
        });
      }
    }


    // 动态计算尺寸方框的最宽宽度
    setTimeout(() => {
      this.calculateMaxBoxWidth();
    }, 0);
    // 1. 定义包装后的防抖函数
    const optimizedResizeWidth = debounce(this.calculateMaxBoxWidth, 200);

    // 2. 开启监听
    window.addEventListener('resize', optimizedResizeWidth);

    // 3. 页面卸载时自动移除
    window.addEventListener('beforeunload', () => {
      window.removeEventListener('resize', optimizedResizeWidth);
    });
  }

  // 处理 from-price-config 第三层价格的展示
  updateFromPriceDiff(variantSku) {
    try {
      const id = `price-${this.dataset.section}`;
      const skuFromPriceId = `#sku-from-price-config-${this.dataset.section}`;
      const destination = document.getElementById(id);
      const skuFromPriceDestination = destination.querySelector(skuFromPriceId);
      if (!skuFromPriceDestination) return;
      const targetSku = skuFromPriceDestination.getAttribute('data-targetSku');
      const fromPriceConfigListStr = skuFromPriceDestination.getAttribute('data-sku-from-price-config');
      const fromPriceConfigList = fromPriceConfigListStr ? JSON.parse(fromPriceConfigListStr) : [];

      showSkuFromPriceConfig(skuFromPriceDestination, variantSku || targetSku);

      const anotherDestination = document.querySelector('product-form.outside .product_form_price');

      if (anotherDestination) {
        showSkuFromPriceConfig(anotherDestination, variantSku || targetSku);
      }

      function showSkuFromPriceConfig(container, sku) {
        try {
          const fromPriceBox = container.querySelector('.price-2lines-from-box');
          const fromPrice = container.querySelector('.price-2lines-from-price');


          if (fromPriceConfigList && fromPriceConfigList?.length) {
            const skuFromPriceInfo = fromPriceConfigList.find((item) => {
              const { sku_list } = item;
              return sku_list.some((s) => s && s?.trim() === sku?.trim());
            });
            if (skuFromPriceInfo && skuFromPriceInfo?.fromPrice) {
              const price = container.querySelector('.price-2lines-price-price');
              fromPrice.innerHTML = extractCurrencySymbol(price.innerHTML) + skuFromPriceInfo.fromPrice;
              fromPriceBox.style.display = 'flex';

            } else {
              fromPrice.innerHTML = '';
              fromPriceBox.style.display = 'none';

            }
          }
        } catch (error) { }

        function extractCurrencySymbol(str) {
          const match = str.match(/\p{Sc}/u);
          return match ? match[0] : null;
        }
      }
    } catch (error) { }
  }

  // 监听 window.currentVariantSku 值的变化
  definePropertyCurrentVariantSkuChange() {
    if (!window.definePropertyCurrentVariantSkuFunList) {
      window.definePropertyCurrentVariantSkuFunList = [];
    }

    window.definePropertyCurrentVariantSkuChange = function (fn) {
      fn && window.definePropertyCurrentVariantSkuFunList.push(fn);
    };

    if (window._currentVariantSkuPropertyDefined) return;
    window._currentVariantSkuPropertyDefined = true;

    Object.defineProperty(window, 'currentVariantSku', {
      set(value) {
        window.definePropertyCurrentVariantSkuFunList.forEach((fn) => fn?.(value));
        this._currentVariantSku = value;
      },
      get() {
        return this._currentVariantSku;
      },
    });
  }

  initColorLevel() {
    const colorField = this.querySelector('.color_field');
    this.colorOptionIdx = colorField ? Number(colorField?.dataset?.index) ?? -1 : -1;
  }

  /**
   * * 计算哪些颜色需要展示出促销标，对应需求:https://youniverse.feishu.cn/docx/UJGedpMAeoE5GHxKvsGcY82xnSc
   */
  initPromotionVariants() {
    this.allOptions = JSON.parse(this.querySelector('#product_options').textContent);
    /** 用于把促销variant按颜色分组，减少计算量 */
    this.promotion_group = {};
    /** 促销的variant数据 */
    this.promotion_variants = JSON.parse(this.querySelector('#promotion_variants').textContent ?? '[]');

    this.allOptions[this.colorOptionIdx]?.values
      .reduce((preVal, color, index) => {
        const matchPromotionVariants = this.promotion_variants.filter((variantItem) => variantItem.options.includes(color));
        if (matchPromotionVariants.length > 0) {
          /** 以颜色为key对促销variant进行分组 */
          this.promotion_group[color] = matchPromotionVariants;
          return [
            ...preVal,
            {
              el: this.querySelector(`#promotion_${this.colorOptionIdx + 1}_${index + 1}`),
              sign: parseShopifyRichTextToHTML(matchPromotionVariants[0]?.sign),
            },
          ];
        }
        return preVal;
      }, [])
      .forEach((promotionData) => {
        promotionData.el.classList.remove('no_promotion');
        if (promotionData.sign) {
          promotionData.el.innerHTML = promotionData.sign;
        }
      });
  }

  // 动态计算尺寸方框的最宽宽度
  calculateMaxBoxWidth() {
    const sizeBoxDom = document.querySelectorAll('.variant-input-wrapper:not(.color_field)');

    sizeBoxDom.forEach(wrapper => {
      const labelDomList = Array.from(wrapper.querySelectorAll('label')).filter(
        (label) => !label.classList.contains('has-size-diagram')
      );

      if (labelDomList.length > 0) {
        let maxWidth = 0;

        labelDomList.forEach(label => {
          label.style.width = 'auto';
          const currentWidth = label.getBoundingClientRect().width;
          if (currentWidth > maxWidth) {
            maxWidth = currentWidth;
          }
        });

        if (maxWidth > 0 && maxWidth < 300) {
          const finalWidth = `${Math.ceil(maxWidth)}px`;
          labelDomList.forEach(label => {
            label.style.width = finalWidth;
            label.style.textAlign = 'center';
          });
        }
      }

      wrapper.querySelectorAll('label.has-size-diagram').forEach((label) => {
        label.style.width = '';
        label.style.textAlign = '';
      });
    });
  }

  updateSizeDiagrams() {
    const configEl = this.querySelector('#size_option_diagrams');
    if (!configEl) return;

    let diagrams = {};
    try {
      diagrams = JSON.parse(configEl.textContent.trim());
    } catch (error) {
      return;
    }

    const fieldsets = this.querySelectorAll('fieldset.size-diagram-field');
    fieldsets.forEach((fieldset) => {
      const optionIndex = Number(fieldset.dataset.index);
      if (Number.isNaN(optionIndex)) return;

      fieldset.querySelectorAll('label.size-diagram-label').forEach((label) => {
        const input = fieldset.querySelector(`#${CSS.escape(label.htmlFor)}`);
        if (!input) return;

        const img = label.querySelector('.size-option-diagram');
        const testOptions = [...this.options];
        testOptions[optionIndex] = input.value;

        const variant = this.getVariantData().find((item) =>
          item.options.every((option, index) => testOptions[index] === option)
        );

        const url = variant ? diagrams[String(variant.id)] || diagrams[variant.id] : '';

        if (url && img) {
          img.src = url;
          label.classList.add('has-size-diagram');
          label.dataset.sizeDiagram = url;
        } else {
          label.classList.remove('has-size-diagram');
          delete label.dataset.sizeDiagram;
          if (img) img.removeAttribute('src');
        }
      });
    });
  }

  onVariantChange(event) {


    this.getProductMedia();
    this.updateOptions();
    this.updateMasterId();
    this.updatePreOrderStatus();
    this.updateDailyActivityStatus();
    this.updateDeleteOptions();
    this.onlyLeg();
    this.lShaped();
    // this.ga4Report(); // 去掉ga4上报 统一使用 google & youtube 插件
    this.toggleAddButton(true, '', false);
    this.updatePickupAvailability();
    this.removeErrorMessage();
    this.validateGang();

    this.updatePriceDiff();

    this.validateMulti();
    this.updateModalPrice();
    this.updateOutOfStockNoticeStatus();

    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true);
      this.setUnavailable();
    } else {
      if (this.gangOption) {
        this.updateGangMedia();
      } else if (this.multi) {
        this.updateMultiMedia({ changedInput: event && event.target });
      } else {
        this.updateMedia();
      }

      // 在搭售弹窗内切换属性不改变url
      if (this.dataset.tplsuffix != 'accessory-modal') {
        this.updateURL();
      }
      this.updateVariantInput();
      this.renderProductInfo();

      this.updateFromPriceDiff(this.currentVariant?.sku);
    }

    if (this.dataset.tplsuffix === 'accessory-modal' && this.currentVariant) {
      const selected = window.UniEventBus.pullData('SELECTED_ACCESSORIES');
      const productData = this.getModalProductdata();
      const isAlreadySelected = selected?.some((item) => Number(item.productId) === Number(productData.id));

      if (isAlreadySelected) {
        const selectAddonButton = this.closest('.html-content')?.querySelector('.select-item-button--select-addon');
        if (selectAddonButton?.classList.contains('selected')) {
          selectAddonButton.classList.add('flicker');
          setTimeout(() => {
            selectAddonButton.classList.remove('flicker');
          }, 500);
        }
      }
    }

    this.handleStickyCart(event.target);
    // 必须先 sync 自动切换兼容规格，再清理仍不兼容的配件；否则会先弹「已移除」再被 default 选回
    if (this.dataset.tplsuffix !== 'accessory-modal' && this.currentVariant) {
      window.UniEventBus.submit('UPDATE_FIT_STATUS', this.currentVariant);
    }
    this.clearSelectedUnfitAccessories();

    this.updateSizeDiagrams();
    this.calculateMaxBoxWidth();
    this.syncSplitOptionFromSource();
    this.updateSplitOptionAvailability();
  }

  /**
   * 将合并 Option（如 Shade | Terrace）拆成两行 UI，选择后写回真实 option radio。
   * 虚拟 UI 不使用 fieldset.variant-input-wrapper，避免干扰 updateOptions。
   */
  initSplitOption() {
    if (this.dataset.splitOption !== 'true' || this.splitUI) return;

    const delimiter = this.dataset.splitDelimiter ?? ' | ';
    const optionIndex = Math.max(0, Number(this.dataset.splitOptionIndex || 3) - 1);
    const fieldsets = Array.from(this.querySelectorAll('fieldset.variant-input-wrapper'));
    const sourceFieldset = fieldsets[optionIndex];
    if (!sourceFieldset) return;

    const radios = Array.from(sourceFieldset.querySelectorAll('input[type="radio"]'));
    const combinedValues = radios.map((radio) => radio.value).filter(Boolean);
    if (!combinedValues.some((value) => value.includes(delimiter))) return;

    const part1Values = [];
    const part2Values = [];
    const seenPart1 = new Set();
    const seenPart2 = new Set();

    combinedValues.forEach((value) => {
      const parsed = this.parseSplitOptionValue(value, delimiter);
      if (!parsed) return;
      if (!seenPart1.has(parsed.part1)) {
        seenPart1.add(parsed.part1);
        part1Values.push(parsed.part1);
      }
      if (!seenPart2.has(parsed.part2)) {
        seenPart2.add(parsed.part2);
        part2Values.push(parsed.part2);
      }
    });

    if (!part1Values.length || !part2Values.length) return;

    this.splitDelimiter = delimiter;
    this.splitSourceFieldset = sourceFieldset;
    this.splitOptionIndex = optionIndex;
    this._splitSyncing = false;

    sourceFieldset.classList.add('split-option-source--hidden');
    sourceFieldset.setAttribute('aria-hidden', 'true');

    const part1Label = this.dataset.splitPart1Label || 'Shade';
    const part2Label = this.dataset.splitPart2Label || 'Terrace';
    const sectionId = this.dataset.section || 'product';

    const buildPart = (partNumber, label, values) => {
      const optionsHtml = values
        .map((value, index) => {
          const inputId = `split_${sectionId}_${optionIndex}_${partNumber}_${index}`;
          return `
            <span class="option_wrapper" data-value="${this.escapeHtmlAttr(value)}">
              <input type="radio" id="${inputId}" name="split-option-${sectionId}-${optionIndex}-${partNumber}" value="${this.escapeHtmlAttr(value)}">
              <label for="${inputId}">${this.escapeHtml(value)}</label>
            </span>
          `;
        })
        .join('');

      return `
        <div class="product-form__input custom-split-option__part" data-split-part="${partNumber}">
          <div class="form__label">
            ${this.escapeHtml(label)}:
            <span class="form__label-info" data-split-label></span>
          </div>
          ${optionsHtml}
        </div>
      `;
    };

    const splitUI = document.createElement('div');
    splitUI.className = 'custom-split-option';
    splitUI.dataset.splitOptionUi = 'true';
    splitUI.innerHTML = `${buildPart(1, part1Label, part1Values)}${buildPart(2, part2Label, part2Values)}`;
    sourceFieldset.insertAdjacentElement('afterend', splitUI);
    this.splitUI = splitUI;

    splitUI.addEventListener('change', (event) => {
      if (!(event.target instanceof HTMLInputElement) || event.target.type !== 'radio') return;
      this.applySplitOptionSelection();
    });

    this.syncSplitOptionFromSource();
    this.updateSplitOptionAvailability();
  }

  parseSplitOptionValue(value, delimiter = this.splitDelimiter || ' | ') {
    if (!value || typeof value !== 'string') return null;
    const index = value.indexOf(delimiter);
    if (index === -1) return null;
    return {
      part1: value.slice(0, index),
      part2: value.slice(index + delimiter.length),
    };
  }

  escapeHtml(text) {
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  escapeHtmlAttr(text) {
    return this.escapeHtml(text).replace(/'/g, '&#39;');
  }

  syncSplitOptionFromSource() {
    if (!this.splitUI || !this.splitSourceFieldset || this._splitSyncing) return;

    const checked = this.splitSourceFieldset.querySelector('input[type="radio"]:checked');
    const parsed = this.parseSplitOptionValue(checked?.value);
    if (!parsed) return;

    this._splitSyncing = true;
    const part1Input = this.splitUI.querySelector(
      `[data-split-part="1"] input[type="radio"][value="${CSS.escape(parsed.part1)}"]`
    );
    const part2Input = this.splitUI.querySelector(
      `[data-split-part="2"] input[type="radio"][value="${CSS.escape(parsed.part2)}"]`
    );
    if (part1Input) part1Input.checked = true;
    if (part2Input) part2Input.checked = true;
    this.updateSplitOptionLabels(parsed.part1, parsed.part2);
    this._splitSyncing = false;
  }

  updateSplitOptionLabels(part1, part2) {
    if (!this.splitUI) return;
    const label1 = this.splitUI.querySelector('[data-split-part="1"] [data-split-label]');
    const label2 = this.splitUI.querySelector('[data-split-part="2"] [data-split-label]');
    if (label1 && part1 != null) label1.textContent = part1;
    if (label2 && part2 != null) label2.textContent = part2;
  }

  applySplitOptionSelection() {
    if (!this.splitUI || !this.splitSourceFieldset || this._splitSyncing) return;

    const part1 = this.splitUI.querySelector('[data-split-part="1"] input[type="radio"]:checked')?.value;
    const part2 = this.splitUI.querySelector('[data-split-part="2"] input[type="radio"]:checked')?.value;
    if (!part1 || !part2) return;

    this.updateSplitOptionLabels(part1, part2);

    const combined = `${part1}${this.splitDelimiter}${part2}`;
    const radios = Array.from(this.splitSourceFieldset.querySelectorAll('input[type="radio"]'));
    let target = radios.find((radio) => radio.value === combined);

    if (!target || target.closest('.option_wrapper')?.classList.contains('hidden')) {
      target = radios.find((radio) => {
        const wrapper = radio.closest('.option_wrapper');
        if (wrapper?.classList.contains('hidden')) return false;
        const parsed = this.parseSplitOptionValue(radio.value);
        return parsed?.part1 === part1;
      });
    }

    if (!target || target.closest('.option_wrapper')?.classList.contains('hidden')) {
      target = radios.find((radio) => !radio.closest('.option_wrapper')?.classList.contains('hidden'));
    }

    if (!target) return;

    if (!target.checked) {
      this._splitSyncing = true;
      target.checked = true;
      this._splitSyncing = false;
      target.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      this.syncSplitOptionFromSource();
      this.updateSplitOptionAvailability();
    }
  }

  updateSplitOptionAvailability() {
    if (!this.splitUI || !this.splitSourceFieldset) return;

    const usableValues = new Set(
      Array.from(this.splitSourceFieldset.querySelectorAll('.option_wrapper:not(.hidden) input[type="radio"]')).map(
        (input) => input.value
      )
    );

    const part1Selected = this.splitUI.querySelector('[data-split-part="1"] input[type="radio"]:checked')?.value;

    this.splitUI.querySelectorAll('[data-split-part="1"] .option_wrapper').forEach((wrapper) => {
      const part1 = wrapper.dataset.value;
      const available = Array.from(usableValues).some((value) => {
        const parsed = this.parseSplitOptionValue(value);
        return parsed?.part1 === part1;
      });
      wrapper.classList.toggle('hidden', !available);
    });

    this.splitUI.querySelectorAll('[data-split-part="2"] .option_wrapper').forEach((wrapper) => {
      const part2 = wrapper.dataset.value;
      const combined = `${part1Selected}${this.splitDelimiter}${part2}`;
      const available = Boolean(part1Selected) && usableValues.has(combined);
      wrapper.classList.toggle('hidden', !available);
    });

    const part2Selected = this.splitUI.querySelector('[data-split-part="2"] input[type="radio"]:checked');
    if (part2Selected?.closest('.option_wrapper')?.classList.contains('hidden')) {
      const firstAvailable = this.splitUI.querySelector(
        '[data-split-part="2"] .option_wrapper:not(.hidden) input[type="radio"]'
      );
      if (firstAvailable) {
        firstAvailable.checked = true;
        this.applySplitOptionSelection();
      }
    }
  }

  /** 更新预售状态 */
  updatePreOrderStatus() {
    if (!this.preOrderBannerEl || !this.preOrderConfig) return;
    if (this.preOrderConfig.some((item) => item.id === this.currentVariant?.id)) {
      this.preOrderBannerEl.classList.remove('hidden');
      this.preOrderBannerEl.innerHTML = this.preOrderConfig.find((item) => item.id === this.currentVariant?.id).text;
    } else {
      this.preOrderBannerEl.classList.add('hidden');
      this.preOrderBannerEl.innerHTML = '';
    }
  }

  /** 更新日常活动 */
  updateDailyActivityStatus() {
    if (!this.dailyActivityBannerEl || !this.dailyActivityConfig) return;
    if (!this.dailyActivityConfig?.variants) {
      this.dailyActivityBannerEl.innerHTML = parseShopifyRichTextToHTML(this.dailyActivityConfig.text);
      this.dailyActivityBannerEl.classList.remove('hidden');
      return;
    }
    if (this.dailyActivityConfig.variants.some((gid) => gid.includes(this.currentVariant?.id))) {
      this.dailyActivityBannerEl.innerHTML = parseShopifyRichTextToHTML(this.dailyActivityConfig.text);
      this.dailyActivityBannerEl.classList.remove('hidden');
    } else {
      this.dailyActivityBannerEl.innerHTML = '';
      this.dailyActivityBannerEl.classList.add('hidden');
    }
  }

  /** 计算部分被删除sku的选项状态 */
  updateDeleteOptions() {
    const curAllUsableOptions = this.getVariantData()
      .filter((variant) => variant.option1 === this.updateOptions()[0])
      .reduce(
        (preVal, variant) => {
          return {
            option2: preVal.option2.add(variant.option2),
            option3: preVal.option3.add(variant.option3),
          };
        },
        {
          option2: new Set(),
          option3: new Set(),
        }
      );

    // 判断当前选中的variant是否可用，不可用选中第一个可用的option组合
    if (!this.currentVariant) {
      for (let idx = 0; idx < this.options.length; idx++) {
        const currentOption = this.options[idx];
        // 第一个选项或当前选项可用时跳过
        if (idx === 0 || curAllUsableOptions[`option${idx + 1}`].has(currentOption)) continue;

        // 当前选项不可用时，获取第一个可用选项
        const firstAvailableOption = [...curAllUsableOptions[`option${idx + 1}`]][0];
        // 找到对应的DOM元素并触发点击（优先真实 option floor，避免命中拆分虚拟 UI）
        const targetInput =
          this.querySelector(
            `[data-option-floor="option${idx + 1}"][data-value="${CSS.escape(firstAvailableOption)}"]`
          ) || this.querySelector(`[data-value="${CSS.escape(firstAvailableOption)}"]`);

        if (targetInput) {
          targetInput.querySelector('input').click();
          return;
        }
      }
    }

    // 隐藏不可用的option2和3
    Object.keys(curAllUsableOptions).forEach((usableOptionKey) => {
      const allCurFloorOptions = this.querySelectorAll(`[data-option-floor="${usableOptionKey}"]`);

      // 先去除全部隐藏状态
      allCurFloorOptions.forEach((el) => el.classList.remove('hidden'));
      const curUsableOptions = curAllUsableOptions[usableOptionKey];
      allCurFloorOptions.forEach((el) => {
        const optionValue = el.getAttribute('data-value');

        if (!curUsableOptions.has(optionValue)) {
          el.classList.add('hidden');
        }
      });
    });

    this.updateSplitOptionAvailability();
  }
  // 单买腿场景隐藏相关属性选项
  /**
   * 对单卖的选项进行特殊处理
   */
  onlyLeg() {
    if (this.currentVariant?.option1.includes('Nur Gestell') || this.currentVariant?.option1?.includes('なし')) {
      this.classList.add('onlyLeg');
      this.classList.remove('notOnlyLeg');
    } else {
      this.classList.remove('onlyLeg');
      this.classList.add('notOnlyLeg');
    }
  }

  // L型桌(l-shaped)场景隐藏相关属性选项
  lShaped() {
    if (this.currentVariant?.option2 == 'Standard') {
      this.classList.remove('l-shaped');
      this.classList.add('standard');
    } else if (this.currentVariant?.option2 == 'L-Shaped' || this.currentVariant?.option2 == 'L-Form') {
      this.classList.remove('standard');
      this.classList.add('l-shaped');
    } else {
      this.classList.remove('standard');
      this.classList.remove('l-shaped');
    }
  }

  ga4Report() {
    if (window.Shopify.country === 'GB') return;

    // 埋点上报
    const eventName = this.dataset.tplsuffix == 'accessory-modal' ? 'view_accessories_item' : 'view_item';

    if (this.currentVariant?.available) {
      const ga4Items = [
        {
          item_id: this.currentVariant.id + '',
          item_name: this.currentVariant.name,
          price: this.currentVariant.price / 100,
          quantity: 1,
        },
      ];
      // window.dataLayer.push({
      //   event: eventName,
      //   ga4: {
      //     items: ga4Items,
      //     value: this.currentVariant.price / 100,
      //   },
      // });
    }
  }

  handleStickyCart(target) {
    const stickyCart = document.getElementById(`sticky-cart-${this.dataset.section}`);
    if (!stickyCart) return;

    const attribute = target.getAttribute('name');

    document
      .querySelectorAll(
        `variant-radios[data-section="${this.dataset.section}"] [name="${attribute}"], variant-selects[data-section="${this.dataset.section}"] [name="${attribute}"]`
      )
      .forEach((input) => {
        if (input.tagName === 'INPUT') {
          input.checked = input.value === target.value;
        } else {
          input.value = target.value;
        }
      });

    if (!this.currentVariant) return;
    if (!this.currentVariant.featured_media) return;

    const image = document.querySelector('.sticky-cart__content-image img');
    if (image) {
      image.srcset = `${this.currentVariant.featured_media.preview_image.src}&width=70 1x, ${this.currentVariant.featured_media.preview_image.src}&width=140 2x`;
      image.src = `${this.currentVariant.featured_media.preview_image.src}&width=70`;
      image.width = 70;
      image.height = Math.ceil(70 / this.currentVariant.featured_media.preview_image.aspect_ratio);
    }
  }

  isCombinationExist(options) {
    return this.getVariantData().find((variant) => {
      return !variant.options
        .map((option, index) => {
          return options[index] === option;
        })
        .includes(false);
    });
  }

  /**
   * 按选项层级判断某候选值是否有货：
   * 固定更早层已选值，当前层代入候选值，后续层自由匹配。
   * 靠前选项（如颜色）不会因当前尺码无货而整行置灰；靠后选项（如尺码）仍按精确组合置灰。
   */
  isOptionValueAvailable(optionIndex, optionValue, selectedOptions = this.updateOptions()) {
    return this.getVariantData().some((variant) => {
      if (!variant.available || this.isUnfitAccessory(variant.sku)) return false;
      if (variant.options[optionIndex] !== optionValue) return false;
      for (let k = 0; k < optionIndex; k++) {
        if (variant.options[k] !== selectedOptions[k]) return false;
      }
      return true;
    });
  }

  isUnfitAccessory(sku) {
    return window.unfit_accessories_data.some(
      (item) => item.accessories_sku.includes(sku) && item.desks_sku.includes(window.mainProductSKU)
    );
  }

  clearSelectedUnfitAccessories() {
    // sticky cart / 配件弹窗也会触发 onVariantChange，避免重复清理
    if (!this.shouldSyncGlobalVariantState()) return;
    // 规格切换中间态可能尚未匹配到有效 variant，此时清理会误删后又被 default 选回
    if (!this.currentVariant?.sku) return;

    const selectedAccessories = window.UniEventBus.pullData('SELECTED_ACCESSORIES');

    if (!selectedAccessories || !selectedAccessories.length) return;

    // 此时 UPDATE_FIT_STATUS → syncAccessoriesToMainVariant 已跑过：
    // 能自动切换的配件 SKU 已更新；这里只移除仍然不兼容的残留项（静默，不弹提示）
    for (const selectedAccessory of [...selectedAccessories]) {
      if (!this.isUnfitAccessory(selectedAccessory.sku)) continue;

      window.UniEventBus.submit('REMOVE_ACCESSORY_ITEM', selectedAccessory);
    }
  }

  updatePriceDiff() {
    if (this.dataset.ismainproduct != '1') return;
    this.allOptions = JSON.parse(this.querySelector('#product_options').textContent);

    for (const item of this.querySelectorAll('.price_diff')) {
      item.innerHTML = '';
    }

    for (const item of this.querySelectorAll('.price_diff--sale')) {
      item.innerHTML = '';
    }

    const selectedOptions = this.updateOptions();

    for (let i = this.allOptions.length - 1; i >= 0; i--) {
      for (let j = 0; j < this.allOptions[i].values.length; j++) {
        let nowPrice = 0;
        if (this.currentVariant) nowPrice = this.currentVariant.price;
        let diff = 0;

        // 精确组合：用于比价 / 当前项判断；可用性按选项层级（后续层自由匹配）
        const matchOptions = [...selectedOptions];
        matchOptions[i] = this.allOptions[i].values[j];

        const matchVariant = this.isCombinationExist(matchOptions);
        const variantStatus = this.isOptionValueAvailable(i, this.allOptions[i].values[j], selectedOptions);
        const isSelf = matchVariant?.id === this.currentVariant?.id;

        const skuOptionInput = this.querySelector(`#sku_option_${this.dataset.section}_${i + 1}_${j + 1}`);
        const skuOptionWrapper = skuOptionInput?.closest?.('.option_wrapper');

        if (matchVariant && this.isUnfitAccessory(matchVariant?.sku)) {
          skuOptionWrapper?.classList.add('unfit');
        } else {
          skuOptionWrapper?.classList.remove('unfit');
        }

        if (variantStatus) {
          // 颜色选项不需要计算比价
          if (i > this.colorOptionIdx && matchVariant) {
            diff = matchVariant.price - nowPrice;
          }
          skuOptionWrapper?.classList.remove('no_match');
        } else {
          skuOptionWrapper?.classList.add('no_match');
        }

        if (
          this.dataset.tplsuffix !== 'accessory-modal' &&
          i === this.allOptions.length - 1 &&
          isSelf &&
          (this.classList.contains('l-shaped') ||
            this.classList.contains('standard') ||
            this.classList.contains('onlyLeg') ||
            this.classList.contains('notOnlyLeg'))
        ) {
          const currentSkuOption = skuOptionWrapper;

          if (this.classList.contains('l-shaped') && !currentSkuOption.classList.contains('lShapedSize')) {
            Array.from(this.querySelectorAll('.thirdOption'))
              .find((el) => el.classList.contains('lShapedSize'))
              ?.querySelector('input')
              .click();
            return;
          } else if (this.classList.contains('standard') && currentSkuOption.classList.contains('lShapedSize')) {
            Array.from(this.querySelectorAll('.thirdOption'))
              .find((el) => !el.classList.contains('lShapedSize'))
              ?.querySelector('input')
              .click();
            return;
          } else if (this.classList.contains('onlyLeg') && !currentSkuOption.classList.contains('legSize')) {
            Array.from(this.querySelectorAll('.secondOption'))
              .find((el) => el.classList.contains('legSize'))
              ?.querySelector('input')
              .click();
            return;
          } else if (this.classList.contains('notOnlyLeg') && currentSkuOption.classList.contains('legSize')) {
            const legSizeDom = Array.from(this.querySelectorAll('.secondOption')).find((el) => !el.classList.contains('legSize'));
            if(legSizeDom){
              legSizeDom?.querySelector('input')
              .click();
              return;
            }
          }
        }


        if (!(this.currentVariant && !this.isUnfitAccessory(this.currentVariant.sku)) && variantStatus) {
          skuOptionInput?.click();
          return;
        }

        const isNegative = 0 > diff;

        if (diff) {
          const foramtPrice =
            diff % 100 == 0
              ? theme.Currency.formatMoney(
                Math.abs(diff),
                shopSettings.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, '{{amount_no_decimals}}')
              )
              : theme.Currency.formatMoney(Math.abs(diff), shopSettings.moneyFormat);

          const price_diff_el = this.querySelector(`#price_diff_${i + 1}_${j + 1}`);
          if (price_diff_el) {
            price_diff_el.classList.remove('underline_text');
            price_diff_el.innerHTML = `${isNegative ? '-' : '+'}${foramtPrice}`;
          }
        }

        const currentColor = this.updateOptions()[this.colorOptionIdx];

        if (i > this.colorOptionIdx && currentColor && this.promotion_group[currentColor]) {
          const isPromoted = this.promotion_group[currentColor].some((saleVariant) => saleVariant.id === matchVariant.id);

          if (isPromoted && !isSelf) {
            const saleDiffPrice = matchVariant.compare_at_price - nowPrice;

            const formatSalePrice =
              saleDiffPrice % 100 == 0
                ? theme.Currency.formatMoney(
                  Math.abs(saleDiffPrice),
                  shopSettings.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, '{{amount_no_decimals}}')
                )
                : theme.Currency.formatMoney(Math.abs(saleDiffPrice), shopSettings.moneyFormat);

            const price_diff_el = this.querySelector(`#price_diff_${i + 1}_${j + 1}`);
            if (price_diff_el) {
              price_diff_el.classList.add('underline_text');
              price_diff_el.innerHTML = `${saleDiffPrice > 0 ? '+' : '-'}${formatSalePrice}`;
            }

            const isNegative = 0 > diff;

            if (diff) {
              const foramtPrice =
                diff % 100 == 0
                  ? theme.Currency.formatMoney(
                    Math.abs(diff),
                    shopSettings.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, '{{amount_no_decimals}}')
                  )
                  : theme.Currency.formatMoney(Math.abs(diff), shopSettings.moneyFormat);
              const price_diff_sale_el = this.querySelector(`#price_diff--sale_${i + 1}_${j + 1}`);
              if (price_diff_sale_el) {
                price_diff_sale_el.innerHTML = `${isNegative ? '-' : '+'}${foramtPrice}`;
              }
            }
          }
        }
      }
    }
  }

  // 更新搭售弹窗内加购按钮的价格
  updateModalPrice() {
    const addButtonPrice = document.querySelector('.select-item-button--add-cart .accessory-price');
    const addCartButton = document.querySelector('.select-item-button--add-cart');
    const selectAddonButton = document.querySelector('.select-item-button--select-addon');
    if (addCartButton && selectAddonButton) {
      this.getModalProductdata();
      const isUnavailable = !this.currentVariant?.available || this.isUnfitAccessory(this.currentVariant?.sku);
      if (isUnavailable) {
        if (addButtonPrice) addButtonPrice.innerHTML = '';
        addCartButton.classList.add('disabled');
        addCartButton.setAttribute('disabled', 'disabled');
        selectAddonButton.classList.add('disabled');
        selectAddonButton.setAttribute('disabled', 'disabled');
      } else {
        addCartButton.classList.remove('disabled');
        addCartButton.removeAttribute('disabled');
        selectAddonButton.classList.remove('disabled');
        selectAddonButton.removeAttribute('disabled');
        if (addButtonPrice) {
          addButtonPrice.innerHTML = theme.Currency.formatMoney(this.currentVariant.price, shopSettings.moneyFormat);
        }
        const selected = window.UniEventBus.pullData('SELECTED_ACCESSORIES');
        const isSelected = selected && selected.map((item) => item.productId).includes(this.productData.id);
        const accessoryAddButton = document.querySelector('.accessory-add-button');
        if (isSelected) {
          accessoryAddButton?.classList.add('selected');
          selectAddonButton.classList.add('selected');
        } else {
          accessoryAddButton?.classList.remove('selected');
          selectAddonButton.classList.remove('selected');
        }
      }
    }
  }

  /**
   * 更新当前组件选中的option组合
   * @returns string[], eg: ['White', '100cm x 70cm']
   */
  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select'), (select) => select.value);
    return this.options;
  }

  /**
   * 更新当前选中variant的各种数据，并同步到window上
   * @returns 当前选中的variant
   */
  updateMasterId() {
    this.currentVariant = findVariantBySelectedOptions(this.getVariantData(), this.options);

    if (this.currentVariant && this.shouldSyncGlobalVariantState()) {
      window.currentVariant = this.currentVariant;
      window.currentVariantId = this.currentVariant.id;
      window.currentVariantAvailable = this.currentVariant.available;
      window.currentVariantPrice = this.currentVariant.price / 100;
      window.currentVariantSku = this.currentVariant.sku;
      window.mainProductSKU = this.currentVariant.sku;
    }

    if (this.dataset.tplsuffix == 'accessory-modal' && this.currentVariant) {
      document.querySelector('.product-content .html-content product-gallery').inAcModalFirstVariant =
        this.currentVariant?.id == JSON.parse(document.querySelector('.html-content #product_data').innerHTML).variants[0].id;
    }


    return this.currentVariant;
  }

  validateGang() {
    const mediaItemWithGang = document.querySelector(`#shopify-section-${this.dataset.section} [data-gang-option]`);
    if (mediaItemWithGang) {
      this.gangOption = mediaItemWithGang.dataset.gangOption;
    }
  }

  validateMulti() {
    const multi = this.getAttribute('multi');
    if (multi == '1') {
      this.multi = true;
    }
  }

  updateGangMedia() {
    if (!this.gangOption) return;

    const variantInputWrapper = this.querySelector('.variant-input-wrapper[data-option-slug="' + this.gangOption + '"]');
    if (variantInputWrapper) {
      this.gangIndex = variantInputWrapper.dataset.optionIndex;
    } else {
      this.gangOption = null;
    }

    if (!this.currentVariant) return;
    //if (!this.currentVariant.featured_media) return;

    const mediaGallery = document.getElementById(`MediaGallery-${this.dataset.section}`);
    const gangValue = this.getSlug(this.currentVariant[this.gangIndex]);
    if (this.currentGangValue !== gangValue) {
      document.querySelectorAll(`#shopify-section-${this.dataset.section} [data-gang-connect]`).forEach(function (media) {
        media.classList.remove('gang__active');
      });

      this.currentGangValue = gangValue;
      var gangConnect = this.gangOption + '_' + gangValue;
      const newMedias = document.querySelectorAll(`#shopify-section-${this.dataset.section} [data-gang-connect="${gangConnect}"]`);

      if (!newMedias.length) return;
      Array.from(newMedias)
        .reverse()
        .forEach(function (newMedia) {
          newMedia.classList.add('gang__active');
          let parent = newMedia.parentElement;
          if (parent.firstChild != newMedia) {
            parent.prepend(newMedia);
            parent.classList.add('product__media-no-animate');
          }
        });

      const thumbnailSlider = document.getElementById(`GalleryThumbnails-${this.dataset.section}`);
      if (thumbnailSlider) {
        thumbnailSlider.updateMediaCount();
        thumbnailSlider.resetPages();
      }

      if (!mediaGallery) {
        const productGallery = document.querySelector(`#shopify-section-${this.dataset.section} product-gallery`);
        if (productGallery) {
          productGallery.resetPages();
          productGallery.goToFirstSlide();
        }
      }

      const gallerySliderButtons = document.querySelector(`#shopify-section-${this.dataset.section} product-gallery .slider-buttons`);
      if (gallerySliderButtons) {
        if (
          document.querySelectorAll(`#shopify-section-${this.dataset.section} product-gallery .product__media-item.gang__active`).length < 2
        ) {
          gallerySliderButtons.classList.add('hidden');
        } else {
          gallerySliderButtons.classList.remove('hidden');
        }
      }
    }

    if (!this.currentVariant.featured_media) {
      if (
        !document.querySelectorAll(`#shopify-section-${this.dataset.section} product-gallery .product__media-item.gang__active.is-active`)
          .length
      ) {
        document
          .querySelector(`#shopify-section-${this.dataset.section} product-gallery .product__media-item.gang__active`)
          .classList.add('is-active');
      }
      return;
    }

    if (mediaGallery) {
      const scrollIntoView = typeof this.dataset.noScroll === 'undefined';
      mediaGallery.setActiveMedia(`${this.dataset.section}-${this.currentVariant.featured_media.id}`, true, scrollIntoView);
    }

    const modalContent = document.querySelector(`#ProductModal-${this.dataset.section} .product-media-modal__content`);
    if (modalContent) {
      const newMediaModal = modalContent.querySelector(`[data-media-id="${this.currentVariant.featured_media.id}"]`);
      modalContent.prepend(newMediaModal);
    }

    const thumbnailContent = document.querySelector(`#ProductThumbnails-${this.dataset.section}`);
    if (thumbnailContent) {
      const newMediaThumbnail = thumbnailContent.querySelector(`[data-media-id="${this.currentVariant.featured_media.id}"]`);
      thumbnailContent.prepend(newMediaThumbnail);
    }
  }

  getSlug(someString) {
    return someString
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-$/, '')
      .replace(/^-/, '');
  }

  updateMultiMedia(utmOptions) {
    if (!this.currentVariant) return;
    if (!this.currentVariant.featured_media) return;
    const currentMediaId = this.currentVariant.featured_media.id;
    const variantMediaIdArr = this.getVariantData()
      .map((item) => item.featured_media?.id)
      .filter(Boolean);
    let start = false;
    const currentMedias = [];
    for (let i = 0; i < this.mediaData.length; i++) {
      if (!start && this.mediaData[i].id == currentMediaId) {
        start = true;
        currentMedias.push(this.mediaData[i].id);
      } else if (start) {
        if (variantMediaIdArr.includes(this.mediaData[i].id)) {
          break;
        } else {
          currentMedias.push(this.mediaData[i].id);
        }
      }
    }

    const sectionSelector = `#shopify-section-${this.dataset.section}`;
    // 按 media id 同步，避免主图 / 缩略图 / spots 数量不一致时按索引取到 undefined
    const syncMultiShow = (nodes, { numbered = false } = {}) => {
      let showCount = 0;
      nodes.forEach((node) => {
        const mediaKey = node.dataset.mediaId || node.dataset.target || node.dataset.spotsId || '';
        const shouldShow = currentMedias.some((id) => mediaKey.includes(String(id)));
        node.className = node.className.replace(/multiShow\d+/g, '');
        if (!shouldShow) {
          node.classList.remove('multi-show');
          return;
        }
        if (numbered) {
          node.classList.add('multi-show', `multiShow${++showCount}`);
        } else {
          node.classList.add('multi-show');
        }
      });
    };

    syncMultiShow(document.querySelectorAll(`${sectionSelector} .product__media-list > li`), { numbered: true });
    syncMultiShow(document.querySelectorAll(`${sectionSelector} .thumbnail-list > li`));
    syncMultiShow(document.querySelectorAll(`${sectionSelector} .spots-list > li`));

    // mobile: 将当前变体主图移到轮播靠前位置
    if (document.body.clientWidth < 750 || this.dataset.tplsuffix == 'accessory-modal') {
      const featuredMediaId = `${this.dataset.section}-${this.currentVariant.featured_media.id}`;

      const mediaList = document.querySelector(`${sectionSelector} .product__media-list`);
      const thumbnailList = document.querySelector(`${sectionSelector} .thumbnail-list`);
      const spotsList = document.querySelector(`${sectionSelector} .spots-list`);

      const featuredSlide = mediaList?.querySelector(`[data-media-id="${featuredMediaId}"]`);
      if (featuredSlide && mediaList.firstChild !== featuredSlide) {
        mediaList.prepend(featuredSlide);
      }

      const featuredThumb = thumbnailList?.querySelector(`[data-target="${featuredMediaId}"]`);
      if (featuredThumb && thumbnailList.firstChild !== featuredThumb) {
        thumbnailList.prepend(featuredThumb);
      }

      const featuredSpot = spotsList?.querySelector(`[data-spots-id="${featuredMediaId}"]`);
      if (featuredSpot && spotsList.firstChild !== featuredSpot) {
        spotsList.prepend(featuredSpot);
      }

      const productGallery = document.querySelector(`${sectionSelector} product-gallery`);
      if (productGallery) {
        productGallery.resetPages();
        productGallery.goToFirstSlide();
      }
    }

    if (window.VernalUtmSceneOverride && typeof window.VernalUtmSceneOverride.apply === 'function') {
      window.VernalUtmSceneOverride.apply(this.dataset.section, utmOptions || {});
    }
  }

  updateMedia() {
    if (!this.currentVariant) return;
    if (!this.currentVariant.featured_media) return;
    const newMedia = document.querySelector(
      `[data-media-id="${this.dataset.section}-${this.currentVariant.featured_media.id}"]`
    );

    if (!newMedia) return;

    const parent = newMedia.parentElement;
    if (parent.firstChild != newMedia) {
      parent.prepend(newMedia);
    }
    parent.classList.add('product__media-no-animate');

    const mediaGallery = document.getElementById(`MediaGallery-${this.dataset.section}`);
    if (mediaGallery) {
      const scrollIntoView = typeof this.dataset.noScroll === 'undefined';
      mediaGallery.setActiveMedia(`${this.dataset.section}-${this.currentVariant.featured_media.id}`, true, scrollIntoView);
    }

    const modalContent = document.querySelector(`#ProductModal-${this.dataset.section} .product-media-modal__content`);
    if (modalContent) {
      const newMediaModal = modalContent.querySelector(`[data-media-id="${this.currentVariant.featured_media.id}"]`);
      modalContent.prepend(newMediaModal);
    }

    const thumbnailContent = document.querySelector(`#ProductThumbnails-${this.dataset.section}`);
    if (thumbnailContent) {
      const newMediaThumbnail = thumbnailContent.querySelector(`[data-media-id="${this.currentVariant.featured_media.id}"]`);
      thumbnailContent.prepend(newMediaThumbnail);
    }
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment, #product-form-${this.dataset.section}--alt`
    );
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      if (productForm.closest('product-form')) {
        productForm.closest('product-form').dataset.name = this.currentVariant.name;
        productForm.closest('product-form').dataset.price = this.currentVariant.price / 100;
      }
    });
  }

  updatePickupAvailability() {
    const pickUpAvailability = document.querySelector('pickup-availability');
    if (!pickUpAvailability || typeof pickUpAvailability.fetchAvailability !== 'function') return;

    if (this.currentVariant && this.currentVariant.available) {
      pickUpAvailability.fetchAvailability(this.currentVariant.id);
    } else {
      pickUpAvailability.removeAttribute('available');
      pickUpAvailability.innerHTML = '';
    }
  }

  removeErrorMessage() {
    const section = this.closest('section');
    if (!section) return;

    section.querySelectorAll('product-form').forEach((productForm) => {
      if (typeof productForm.handleErrorMessage === 'function') productForm.handleErrorMessage();
    });
  }

  renderProductInfo() {
    if (this.currentVariant) {
      const { sku, price, compare_at_price, option1, option2, option3 } = this.currentVariant;
      this.updateSKU(sku, true);
      const isAccessoryModal = this.dataset.tplsuffix === 'accessory-modal';
      const selectedAccessories = isAccessoryModal
        ? []
        : window.UniEventBus.pullData('SELECTED_ACCESSORIES') || [];
      const display = buildCombinedProductDisplayPrice(price, compare_at_price, selectedAccessories);
      this.updatePrice(
        display.formattedPrice,
        true,
        display.formattedComparePrice,
        display.compareAtCents || 0,
        display.priceCents
      );
      this.updateColorName([option1, option2, option3], true);
    }

    fetch(`${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`)
      .then((response) => response.text())
      .then((responseText) => {
        this.updatePriceAlt(responseText);
        this.updateInventoryStatus(responseText);
        if (this.currentVariant) {
          this.toggleAddButton(!this.currentVariant.available, this.hasExpectedDate ? 'Coming Soon' : variantStrings.soldOut);
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }

  updateSKU(data, useProductData = false) {
    const id = `sku-${this.dataset.section}`;
    const destination = document.getElementById(id);

    if (useProductData && data && destination) {
      destination.innerHTML = data;
    } else {
      const html = new DOMParser().parseFromString(data, 'text/html');
      const source = html.getElementById(id);
      if (source && destination) destination.innerHTML = source.innerHTML;
    }
    if (destination) destination.classList.remove('visibility-hidden');
  }

  updatePrice(data, useProductData = false, comparePrice, compareAtCents, priceCents) {
    const id = `price-${this.dataset.section}`;
    const destination = document.getElementById(id);
    const isAccessoryModal = this.dataset.tplsuffix === 'accessory-modal';
    const anotherDestination = isAccessoryModal
      ? null
      : document.querySelector('product-form.outside .product_form_price');

    const updateDiscountBadge = (container, compareAt, price) => {
      if (!container) return;

      const priceContainer = container.querySelector('.price-2lines') || container;
      let badgesWrap = priceContainer.querySelector('.price-2lines-badges');
      let badge = priceContainer.querySelector('.price-2lines-discount-badge');
      const template =
        badge?.dataset?.discountTemplate || priceContainer.dataset?.discountTemplate;
      if (!template) return;

      const hasSavings = compareAt && compareAt > price;
      const showIcon =
        (badge?.dataset?.discountBadgeShowIcon || priceContainer.dataset?.discountBadgeShowIcon) ===
        'true';
      const DISCOUNT_BADGE_ICON_SVG = `
        <svg class="price-2lines-discount-badge__icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <path d="M7.8377 13.2L13.2002 7.8375L5.3627 0H0.000195503V5.3625L7.8377 13.2ZM7.8377 12.045L0.825194 5.0325V0.825H5.0327L12.0452 7.8375L7.8377 12.045ZM3.30019 2.475C2.80519 2.475 2.4752 2.805 2.4752 3.3C2.4752 3.795 2.80519 4.125 3.30019 4.125C3.79519 4.125 4.1252 3.795 4.1252 3.3C4.1252 2.805 3.79519 2.475 3.30019 2.475Z" fill="currentColor"/>
        </svg>
      `;

      if (!badgesWrap) {
        badgesWrap = document.createElement('div');
        badgesWrap.className = 'price-2lines-badges';
        if (priceContainer.querySelector('.price-2lines-promo-entry')) {
          badgesWrap.classList.add('price-2lines-badges--below');
        }
        const priceEl = priceContainer.querySelector('.price-2lines-price');
        if (priceEl) {
          priceEl.after(badgesWrap);
        } else {
          priceContainer.appendChild(badgesWrap);
        }
      }

      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'price-2lines-discount-badge';
        badge.dataset.discountTemplate = template;
        badge.dataset.discountFormat =
          priceContainer.dataset.discountFormat || 'amount';
        badge.dataset.discountBadgeShowIcon = showIcon ? 'true' : 'false';
        badge.style.backgroundColor =
          priceContainer.dataset.discountBadgeBgColor || '#FFF4CC';
        badge.style.color = priceContainer.dataset.discountBadgeTextColor || '#202020';
        badge.innerHTML = `${showIcon ? DISCOUNT_BADGE_ICON_SVG : ''}<span class="price-2lines-discount-badge__text"></span>`;
        badgesWrap.prepend(badge);
      }

      const textEl =
        badge.querySelector('.price-2lines-discount-badge__text') || badge;

      if (hasSavings) {
        const savings = compareAt - price;
        const formattedSavings = theme.Currency.formatMoney(savings, shopSettings.moneyFormat);
        const percent = Math.round((savings / compareAt) * 100);
        textEl.textContent = template
          .replace(/\[amount\]/g, formattedSavings)
          .replace(/\[percent\]/g, String(percent));
        badge.removeAttribute('hidden');
      } else {
        textEl.textContent = '';
        badge.setAttribute('hidden', '');
      }
    };

    if (useProductData && data && destination) {
      const target = destination.querySelector('.price-2lines-price-price');
      target.innerHTML = `
        ${data}
      `;

      // 更新划线价，如果有划线价的话
      if (comparePrice) {
        destination.querySelector('.price-2lines-compare-at').classList.remove('no-compare');
        destination.querySelector('.price-2lines-compare-at-price').innerHTML = `
          ${comparePrice}
        `;
      } else {
        destination.querySelector('.price-2lines-compare-at').classList.add('no-compare');
      }

      updateDiscountBadge(destination, compareAtCents, priceCents);

      if (anotherDestination) {
        if (comparePrice) {
          anotherDestination.querySelector('.price-2lines-compare-at').classList.remove('no-compare');
          anotherDestination.querySelector('.price-2lines-compare-at-price').innerHTML = `
            ${comparePrice}
          `;
        } else {
          anotherDestination.querySelector('.price-2lines-compare-at').classList.add('no-compare');
        }

        anotherDestination.querySelector('.price-2lines-price-price').innerHTML = `
          ${data}
        `;

        updateDiscountBadge(anotherDestination, compareAtCents, priceCents);
      }
    } else {
      const html = new DOMParser().parseFromString(data, 'text/html');
      const source = html.getElementById(id);

      if (source && destination) destination.innerHTML = source.innerHTML;
      if (source && anotherDestination) anotherDestination.innerHTML = source.innerHTML;
    }
    if (destination) destination.classList.remove('visibility-hidden');
  }

  updatePriceAlt(responseText, useProductData = false) {
    const id = `price-${this.dataset.section}--alt`;
    const html = new DOMParser().parseFromString(responseText, 'text/html');
    const destination = document.getElementById(id);
    const source = html.getElementById(id);

    if (source && destination) destination.innerHTML = source.innerHTML;
    if (destination) destination.classList.remove('visibility-hidden');
  }

  // updateOptionsName
  updateColorName(data, useProductData = false) {
    const id = `color-${this.dataset.section}`;
    const destination = document.querySelectorAll(`#${id}`);

    if (useProductData && data && destination.length) {
      for (let i = 0; i < destination.length; i++) {
        destination[i].innerHTML = data[i];
        destination[i].classList.remove('visibility-hidden');
      }
    } else {
      const html = new DOMParser().parseFromString(data, 'text/html');
      const source = html.querySelectorAll(`#${id}`);

      if (source.length && destination.length) {
        for (let i = 0; i < destination.length; i++) {
          destination[i].innerHTML = source[i].innerHTML;
          destination[i].classList.remove('visibility-hidden');
        }
      }
    }
  }

  updateInventoryStatus(responseText, useProductData = false) {
    const id = `inventory-${this.dataset.section}`;
    const html = new DOMParser().parseFromString(responseText, 'text/html');
    const destination = document.getElementById(id);
    const source = html.getElementById(id);

    if (source && destination) destination.innerHTML = source.innerHTML;
    if (destination) destination.classList.remove('visibility-hidden');
  }

  toggleAddButton(disable = true, text, modifyClass = true, useProductData = false) {
    const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-${this.dataset.section}--alt`);
    productForms.forEach((productForm) => {
      const addButton = productForm.querySelector('[name="add"]');
      if (!addButton) return;

      if (disable) {
        addButton.setAttribute('disabled', true);
        if (text) addButton.textContent = text;
      } else {
        addButton.removeAttribute('disabled');

        const { price, compare_at_price } = this.currentVariant;
        const selectedAccessories = window.UniEventBus.pullData('SELECTED_ACCESSORIES') || [];
        addButton.innerHTML = this.preOrderConfig.some((item) => item.id === this.currentVariant.id)
          ? variantStrings.preOrder
          : formatAddToCartButtonText(price, selectedAccessories, compare_at_price);
      }

      if (!modifyClass) return;
    });
  }

  setUnavailable() {
    const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-${this.dataset.section}--alt`);
    productForms.forEach((productForm) => {
      const addButton = productForm.querySelector('[name="add"]');
      if (!addButton) return;
      addButton.textContent = variantStrings.unavailable;

      const price = document.getElementById(`price-${this.dataset.section}`);
      if (price) price.classList.add('visibility-hidden');

      const priceAlt = document.getElementById(`price-${this.dataset.section}--alt`);
      if (priceAlt) priceAlt.classList.add('visibility-hidden');

      const inventory = document.getElementById(`inventory-${this.dataset.section}`);
      if (inventory) inventory.classList.add('visibility-hidden');
    });
  }

  /**
   * 获取商品的所有的variant数据
   * @returns 该商品的所有variant数据
   */
  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }

  getProductMedia() {
    this.mediaData = this.mediaData || JSON.parse(this.querySelector('#product_media').textContent);
  }

  // 获取搭售弹窗内商品数据
  getModalProductdata() {
    this.productData = JSON.parse(document.querySelector('.html-content #product_data').textContent);
    return this.productData;
  }

  getExpectedNoticeOverrides(noticeEl) {
    if (this.expectedNoticeOverrides !== undefined) return this.expectedNoticeOverrides;

    const overridesEl =
      noticeEl?.querySelector('.expected-notice-overrides-data') ||
      document.querySelector('#expected_sales_notice_overrides');

    if (!overridesEl) {
      this.expectedNoticeOverrides = [];
      return this.expectedNoticeOverrides;
    }

    try {
      const parsed = JSON.parse(overridesEl.textContent.trim() || '[]');
      this.expectedNoticeOverrides = Array.isArray(parsed) ? parsed : [parsed];
    } catch (error) {
      console.warn('Invalid expected notice overrides JSON:', error);
      this.expectedNoticeOverrides = [];
    }

    return this.expectedNoticeOverrides;
  }

  /** 操作断货提醒 */
  updateOutOfStockNoticeStatus() {
    this.expectedTimeById = this.expectedTimeById || JSON.parse(document.querySelector('#expected_sales_time').textContent);

    const noticeEl = document.querySelector('.expected-sales-notice');
    if (!noticeEl) return;

    if (this.currentVariant.available) {
      noticeEl?.classList.add('hidden');
      this.hasExpectedDate = false;
    } else {
      const shopKey = window.shop_country;
      const override = this.getExpectedNoticeOverrides(noticeEl).find(
        (item) => Number(item.variant) === this.currentVariant.id
      );

      noticeEl?.classList.remove('hidden');
      let expectedTimeText = '';

      if (override?.text) {
        expectedTimeText = override.text;
        this.hasExpectedDate = true;
      } else {
        const expectedItem = this.expectedTimeById.find((item) => item.id === this.currentVariant.id);
        const expectedDate = this.computeExpectedTime(expectedItem?.expected_sales_time, shopKey);
        this.hasExpectedDate = expectedDate ? true : false;

        switch (shopKey) {
          case 'de':
            expectedTimeText = expectedDate
              ? `Dieses Produkt, das derzeit nicht auf Lager ist, wird voraussichtlich ${expectedDate.day < 10 ? 'Anfang' : expectedDate.day > 21 ? 'Ende' : 'Mitte'
              } ${expectedDate.month} ${expectedDate.year} verfügbar sein.`
              : 'Leider ist keine Versandinformationen verfügbar.';
            break;
          case 'us':
            expectedTimeText = expectedDate
              ? `The product currently out of stock is expected to be available ${expectedDate.day < 10 ? 'at the beginning of' : expectedDate.day > 21 ? 'at end of' : 'in the middle of'
              } ${expectedDate.month} ${expectedDate.year}`
              : 'Unfortunately, no ETA information is available.';
            break;
          case 'uk':
            expectedTimeText = expectedDate
              ? `The product currently out of stock is expected to be available ${expectedDate.day < 10 ? 'at the beginning of' : expectedDate.day > 21 ? 'at end of' : 'in the middle of'
              } ${expectedDate.month} ${expectedDate.year}`
              : 'Unfortunately, no ETA information is available.';
            break;
          case 'jp':
            expectedTimeText = expectedDate
              ? `現在品切れ中の商品は${expectedDate.year}年${expectedDate.month}月${expectedDate.day < 10 ? '上旬' : expectedDate.day > 21 ? '下旬' : '中旬'
              }に入荷予定です。`
              : '出荷情報はありません';
            break;
          default:
            expectedTimeText = expectedDate
              ? `The product currently out of stock is expected to be available ${expectedDate.month} ${expectedDate.year}`
              : 'Unfortunately, no ETA information is available.';
            break;
        }
      }

      noticeEl.querySelector('.expected-sales-notice__text').textContent = expectedTimeText;
    }
  }

  computeExpectedTime(time, shopKey) {
    const monthMap = {
      1: 'Jan.',
      2: 'Feb.',
      3: 'Mar.',
      4: 'Apr.',
      5: 'May',
      6: 'Jun.',
      7: 'Jul.',
      8: 'Aug.',
      9: 'Sep.',
      10: 'Oct.',
      11: 'Nov.',
      12: 'Dec.',
    };
    try {
      if (!time) throw new Error('not current sku expected time');
      const currentDate = new Date();
      if (currentDate.getTime() >= time) {
        throw new Error('current sku expected time less than current time');
      }

      const expectedDate = new Date(Number(time));
      return {
        year: expectedDate.getFullYear(),
        month: shopKey === 'jp' ? expectedDate.getMonth() + 1 : monthMap[expectedDate.getMonth() + 1],
        day: expectedDate.getDate(),
      };
    } catch (error) {
      console.warn(error);
      return null;
    }
  }
}
customElements.define('variant-selects', VariantSelects);
class VariantRadios extends VariantSelects {
  constructor() {
    super();
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset.variant-input-wrapper'));
    this.options = fieldsets.map((fieldset) => {
      const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
      return radios.find((radio) => radio.checked)?.value ?? radios[0]?.value;
    });

    return this.options;
  }
}
customElements.define('variant-radios', VariantRadios);

const generateId = () => (Math.random().toFixed(10) * 10000000000).toFixed() + new Date().getTime();

/** mini-cart 自定义元素可能尚未 upgrade（页面加载中加购）；取已具备 API 的实例 */
function getReadyMiniCart() {
  const el = document.querySelector('mini-cart');
  if (el && typeof el.renderContents === 'function' && typeof el.getSectionsToRender === 'function') {
    return el;
  }
  return null;
}

/** 等待 mini-cart 就绪后再渲染，避免加载中加购导致侧栏空态/列表叠层错乱 */
function renderMiniCartWhenReady(parsedState, isOpenDrawer = true) {
  const run = (miniCart) => {
    if (miniCart && parsedState) miniCart.renderContents(parsedState, isOpenDrawer);
  };
  const ready = getReadyMiniCart();
  if (ready) {
    run(ready);
    return Promise.resolve(ready);
  }
  if (!window.customElements || typeof customElements.whenDefined !== 'function') {
    return Promise.resolve(null);
  }
  return Promise.race([
    customElements.whenDefined('mini-cart'),
    new Promise((resolve) => window.setTimeout(resolve, 4000)),
  ]).then(() => {
    const miniCart = getReadyMiniCart();
    run(miniCart);
    return miniCart;
  });
}

window.getReadyMiniCart = getReadyMiniCart;
window.renderMiniCartWhenReady = renderMiniCartWhenReady;

class ProductForm extends HTMLElement {
  constructor() {
    super();

    this.overProductAreaObserve = null;
    this.noObserve = this.getAttribute('data-noObserve');
    this.form = this.querySelector('form');
    this.form.querySelector('[name=id]').disabled = false;
    this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
    this.scrollOverProductAreaHandle = this.scrollOverProductAreaHandle.bind(this);
    this.initObserver();
    this.addEventListener('click', this.onErrorRecoveryClick.bind(this));
  }

  get miniCart() {
    return getReadyMiniCart();
  }

  syncSectionErrorMessages(errorMessage = false) {
    const section = this.closest('section');
    if (!section) return;

    section.querySelectorAll('product-form').forEach((form) => {
      if (form !== this && typeof form.handleErrorMessage === 'function') {
        form.handleErrorMessage(errorMessage);
      }
    });
  }

  resetCopyIconState(copyButton) {
    if (!copyButton) return;
    copyButton.classList.remove('is-copied');
    const copyIcon = copyButton.querySelector('.product-form__error-code-copy-icon');
    const checkIcon = copyButton.querySelector('.product-form__error-code-check-icon');
    if (copyIcon) copyIcon.removeAttribute('hidden');
    if (checkIcon) checkIcon.setAttribute('hidden', '');
  }

  onErrorRecoveryClick(event) {
    const refreshButton = event.target.closest('.product-form__error-recovery-button');
    if (refreshButton) {
      event.preventDefault();
      deleteCookie('cart');
      window.location.reload();
      return;
    }

    const copyButton = event.target.closest('.product-form__error-code-copy');
    if (copyButton) {
      event.preventDefault();
      const codeValue = this.querySelector('.product-form__error-code-value');
      const text = codeValue ? codeValue.textContent.trim() : '';
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        navigator.clipboard
          .writeText(text)
          .then(() => {
            const copyIcon = copyButton.querySelector('.product-form__error-code-copy-icon');
            const checkIcon = copyButton.querySelector('.product-form__error-code-check-icon');
            if (copyIcon) copyIcon.setAttribute('hidden', '');
            if (checkIcon) checkIcon.removeAttribute('hidden');
            copyButton.classList.add('is-copied');
            if (this._copySuccessTimeout) clearTimeout(this._copySuccessTimeout);
            this._copySuccessTimeout = setTimeout(() => {
              this.resetCopyIconState(copyButton);
            }, 2000);
          })
          .catch(() => {});
      }
    }
  }

  disconnectedCallback() {
    if (this.overProductAreaObserve) {
      this.overProductAreaObserve.disconnect();
    }
  }

  initObserver() {
    if (window.innerWidth < 750 && !this.noObserve) {
      this.style.visibility = 'hidden';
      const observeElement = document.querySelector('.product');
      if (observeElement) {
        this.overProductAreaObserve = new IntersectionObserver(this.scrollOverProductAreaHandle);
        this.overProductAreaObserve.observe(observeElement);
      }
    }
  }

  getCustomLineOptionRoot() {
    const formId = this.form?.getAttribute('id') || '';
    const sectionId = formId.replace(/^product-form-/, '').replace(/--alt$/, '');
    return (
      document.getElementById(`ProductInfo-${sectionId}`) ||
      this.closest('.product__info-container') ||
      this.closest('.shopify-section') ||
      document
    );
  }

  collectCustomLineOptionProperties() {
    const properties = {};
    const root = this.getCustomLineOptionRoot();
    root.querySelectorAll('[data-custom-line-option]').forEach((wrap) => {
      const name = wrap.dataset.optionName;
      const checked = wrap.querySelector('input[type="radio"]:checked');
      if (name && checked?.value) {
        properties[name] = checked.value;
      }
    });
    return properties;
  }

  validateCustomLineOptions() {
    const root = this.getCustomLineOptionRoot();
    for (const wrap of root.querySelectorAll('[data-custom-line-option][data-required="true"]')) {
      const checked = wrap.querySelector('input[type="radio"]:checked');
      if (!checked?.value) {
        return wrap.dataset.optionName || 'option';
      }
    }
    return null;
  }

  async onSubmitHandler(evt) {
    if (document.body.classList.contains('template-cart') || !shopSettings.cartDrawer) {
      return;
    }

    evt.preventDefault();
    const submitButton = this.querySelector('[type="submit"]');
    if (submitButton.classList.contains('loading')) {
      return;
    }

    this.handleErrorMessage();
    this.syncSectionErrorMessages();

    const missingCustomOption = this.validateCustomLineOptions();
    if (missingCustomOption) {
      this.handleErrorMessage(`Please select ${missingCustomOption}`);
      this.syncSectionErrorMessages(`Please select ${missingCustomOption}`);
      return;
    }

    submitButton.setAttribute('aria-disabled', true);
    submitButton.classList.add('loading');

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData(this.form);

    const miniCartForSections = this.miniCart;
    if (miniCartForSections) {
      formData.append(
        'sections',
        miniCartForSections.getSectionsToRender().map((section) => section.section)
      );
    } else {
      // mini-cart 尚未 upgrade 时仍请求 section，便于就绪后渲染
      formData.append('sections', 'mini-cart,cart-icon-bubble');
    }
    formData.append('sections_url', window.location.pathname);
    config.body = formData;

    const selectedAccessories = window.UniEventBus.pullData('SELECTED_ACCESSORIES') || [];

    // 埋点上报相关数据
    const ga4Items = [
      {
        item_name: this.dataset.name || '',
        item_id: formData.get('id'),
        price: this.dataset.price,
        quantity: 1,
      },
    ];

    const assemblyServiceSelected = window.UniEventBus.pullData('ASSEMBLY_SERVICE_SELECTED');

    let cartCookieId = '';
    if (typeof window.__vernalSensorsEnsureCookieIdForCart === 'function') {
      cartCookieId = (await window.__vernalSensorsEnsureCookieIdForCart()) || '';
    } else if (typeof window.__vernalSensorsEnsurePresetPropertiesForCart === 'function') {
      await window.__vernalSensorsEnsurePresetPropertiesForCart();
      cartCookieId = getCookie('ver_sc_cid') || '';
    } else {
      cartCookieId = getCookie('ver_sc_cid') || '';
    }
    const sensorsPresetPayload =
      typeof window.__vernalSensorsGetPresetPropertiesForCart === 'function'
        ? window.__vernalSensorsGetPresetPropertiesForCart()
        : {};

    const customLineOptionProperties = this.collectCustomLineOptionProperties();

    const properties = {
      _combineConfig: {
        combineId: generateId(),
        combineType: 'product',
        itemType: 'product',
      },
      _version: window.commentPropertiesVersion,
      _productId: +this.dataset.id,
      _actualProductId: +this.dataset.id,
      _cookieId: cartCookieId,
      _skuId: +formData.get('id'),
      _productCombineId: generateId(),
      _fulfillmentGroupId: generateId(),
      ...(window.deskSelectorSku ? { _deskSelectorSku: window.deskSelectorSku } : {}),
      ...(assemblyServiceSelected ? { _assemblyService: true } : {}),
      ...sensorsPresetPayload,
      ...customLineOptionProperties,
    };

    const items = selectedAccessories.map((item) => {
      ga4Items.push({
        item_name: item.productHandle,
        item_id: item.variantId,
        price: String(item.price),
        quantity: 1,
      });
      return {
        id: +item.variantId,
        quantity: 1,
        properties: {
          _combineConfig: {
            combineId: generateId(),
            combineType: 'product',
            itemType: 'accessories',
          },
          _version: window.commentPropertiesVersion,
          _productId: +item.productId,
          _actualProductId: +item.productId,
          _cookieId: cartCookieId,
          _skuId: +item.variantId,
          _productCombineId: generateId(),
          _fulfillmentGroupId: generateId(),
          ...sensorsPresetPayload,
        },
      };
    });
    items.push({
      id: +formData.get('id'),
      quantity: 1,
      properties,
    });

    const JSONData = { items };
    formData.forEach(function (value, key) {
      if (key != 'id') JSONData[key] = value;
    });

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(JSONData),
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.handleErrorMessage(response.description);
          this.syncSectionErrorMessages(response.description);
          return;
        }

        // Sensors: AddToCart (仅当 UK 神策脚本加载时才上报)
        try {
          const sensors = window.sensorsDataAnalytic201505 || window['sensorsDataAnalytic201505'];
          if (sensors && typeof sensors.track === 'function') {
            const currency = (window.shopSettings && window.shopSettings.isoCode) || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';
            const currentPageName = document.title || '';
            const cartItems = Array.isArray(response.items) ? response.items : [];

            (JSONData.items || []).forEach((reqItem) => {
              const variantId = Number(reqItem.id);
              const quantity = Number(reqItem.quantity || 0);
              if (!variantId || !quantity) return;

              const matched = cartItems.find((ci) => Number(ci.id) === variantId);
              const productId =
                (matched && (matched.product_id || matched.productId) && Number(matched.product_id || matched.productId)) ||
                (reqItem.properties && (reqItem.properties._productId || reqItem.properties.productId) && Number(reqItem.properties._productId || reqItem.properties.productId)) ||
                null;
              const productName =
                (matched && (matched.product_title || matched.productTitle || matched.title) && String(matched.product_title || matched.productTitle || matched.title)) ||
                (variantId === Number(formData.get('id')) ? String(this.dataset.name || '') : '') ||
                '';
              const priceCents = matched ? Number(matched.final_price ?? matched.price ?? 0) : 0;
              const displayPrice = Number.isFinite(priceCents) ? priceCents / 100 : 0;

              if (!productId || !productName) return;

              sensors.track('AddToCart', {
                product_id: String(productId),
                variant_id: String(variantId),
                product_name: productName,
                display_price: displayPrice,
                curency: currency,
                current_page_name: currentPageName,
                ysku_quantity: quantity,
              });
            });
          }
        } catch (e) {
          // ignore
        }

        renderMiniCartWhenReady(response, true);

        window.UniEventBus.submit('RESET_ACCESSORIES_AFTER_ADD_TO_CART');
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        submitButton.classList.remove('loading');
        submitButton.removeAttribute('aria-disabled');
      });
  }

  scrollOverProductAreaHandle(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        this.style.visibility = 'hidden';
      } else {
        this.style.visibility = 'visible';
      }
    });
  }

  handleErrorMessage(errorMessage = false) {
    this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');

    if (this.errorMessageWrapper) {
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');
      this.errorRecovery = this.errorRecovery || this.errorMessageWrapper.querySelector('.product-form__error-recovery');
      this.errorCodeValue = this.errorCodeValue || this.errorMessageWrapper.querySelector('.product-form__error-code-value');
      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
        const showRecovery = /sold out|ausverkauft/i.test(errorMessage);
        if (this.errorRecovery) {
          this.errorRecovery.toggleAttribute('hidden', !showRecovery);
        }
        if (this.errorCodeValue && showRecovery) {
          this.errorCodeValue.textContent = getCookie('cart') || '-';
        }
      } else {
        if (this.errorRecovery) {
          this.errorRecovery.setAttribute('hidden', '');
        }
        this.querySelectorAll('.product-form__error-code-copy.is-copied').forEach((btn) => {
          this.resetCopyIconState(btn);
        });
        if (this._copySuccessTimeout) {
          clearTimeout(this._copySuccessTimeout);
          this._copySuccessTimeout = null;
        }
      }
    } else {
      if (errorMessage) {
        alert(errorMessage);
      }
    }
  }
}
customElements.define('product-form', ProductForm);

document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
  const wrap = input.closest('[data-custom-line-option]');
  if (!wrap) return;
  const label = wrap.querySelector('[data-custom-line-option-label]');
  if (label) label.textContent = input.value;
});

class ProgressBar extends HTMLElement {
  constructor() {
    super();

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 0,
    });
  }

  init() {
    setTimeout(() => {
      const quantity = parseInt(this.dataset.quantity);
      this.style.setProperty('--progress-bar-width', `${(quantity * 100) / 30}%`);
    }, 1e3);
  }
}
customElements.define('progress-bar', ProgressBar);

class FormState extends HTMLElement {
  constructor() {
    super();

    this.formInputs = this.querySelectorAll('input,select,textarea');
    this.form = this.querySelector('form');

    this.formInputs.forEach((input) => {
      input.addEventListener('input', this.onInputChange.bind(this));
      input.addEventListener('blur', this.onInputChange.bind(this));
    });

    if (this.form) this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
  }

  onInputChange(event) {
    this.handleInputCheck(event.target);
  }

  onSubmitHandler(event) {
    let valid = !0;

    this.formInputs.forEach((input) => {
      if (!this.handleInputCheck(input)) {
        valid = !1;
      }
    });

    if (!valid) {
      event.preventDefault();
      return;
    }
  }

  handleInputCheck(input) {
    if (input.classList.contains('required')) {
      if (input.value.length === 0 || input.value === input.dataset.empty) {
        input.classList.remove('valid');
        input.classList.add('invalid');

        return !1;
      } else {
        input.classList.remove('invalid');
        input.classList.add('valid');

        return !0;
      }
    }

    return !0;
  }
}
customElements.define('form-state', FormState);

class ListMenuItem extends HTMLElement {
  constructor() {
    super();

    this.listMenuHover = this.closest('menu-drawer').querySelector('list-menu-hover');

    this.addEventListener('mouseenter', () => {
      this.listMenuHover.open({
        title: this.dataset.title,
        url: this.dataset.url,
        image: this.dataset.image,
        description: this.dataset.description,
      });
    });
  }
}
customElements.define('list-menu-item', ListMenuItem);

class ListMenuHover extends HTMLElement {
  constructor() {
    super();
  }

  open(obj) {
    let html = '';
    const opened = obj.image.length > 0 || obj.description.length > 0;

    if (opened) {
      html += `<a href="${obj.url}" class="hover-collection${obj.image.length > 0 ? '' : ' hover-collection--no-image'}">`;
      html +=
        '<figure class="hover-collection__image"' +
        (obj.image.length > 0 ? `style="background-image:url(${obj.image})"` : '') +
        '></figure>';
      html += '<div class="hover-collection__content">';
      html += `<span class="hover-collection__title h4">${obj.title}</span>`;
      html += obj.description.length > 0 ? `<div class="hover-collection__description">${obj.description}</div>` : '';
      html += '</div>';
      html += '</a>';
    }

    this.innerHTML = html;
  }

  close() {
    this.innerHTML = '';
  }
}
customElements.define('list-menu-hover', ListMenuHover);

class TransparentHeader extends HTMLElement {
  constructor() {
    super();

    const header = document.getElementById('shopify-section-header');

    header.classList.add('transparent-header');
    if (this.dataset.showSeparator == 'true') {
      header.classList.add('transparent-separator');
    }
  }
}
customElements.define('transparent-header', TransparentHeader);

class ColorSwatch extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('mouseenter', this.onHoverHandler.bind(this));
  }

  onHoverHandler() {
    const image = this.closest('.card-wrapper').querySelector('.media img');

    if (image !== null) {
      image.src = this.dataset.src;
      image.srcset = this.dataset.srcset;

      this.activeColorSwatch();
    }
  }

  activeColorSwatch() {
    this.closest('.card__colors')
      .querySelectorAll('.color-swatch')
      .forEach((color) => {
        color.classList.remove('active');
      });

    this.parentNode.classList.add('active');
  }
}
customElements.define('color-swatch', ColorSwatch);

class AddToCart extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', this.onClickHandler.bind(this));
  }

  get miniCart() {
    return getReadyMiniCart();
  }

  onClickHandler() {
    const variantId = this.dataset.variantId;

    if (variantId) {
      if (document.body.classList.contains('template-cart') || !shopSettings.cartDrawer) {
        Shopify.postLink(routes.cart_add_url, {
          parameters: {
            id: variantId,
            quantity: 1,
          },
        });
        return;
      }

      this.setAttribute('disabled', true);
      this.classList.add('loading');
      const sections = this.miniCart
        ? this.miniCart.getSectionsToRender().map((section) => section.section)
        : ['mini-cart', 'cart-icon-bubble'];

      const body = JSON.stringify({
        id: variantId,
        quantity: 1,
        sections: sections,
        sections_url: window.location.pathname,
      });

      fetch(`${routes.cart_add_url}`, { ...fetchConfig('javascript'), body })
        .then((response) => response.json())
        .then((parsedState) => {
          // Sensors: AddToCart (仅当 UK 神策脚本加载时才上报)
          try {
            const sensors = window.sensorsDataAnalytic201505 || window['sensorsDataAnalytic201505'];
            if (sensors && typeof sensors.track === 'function') {
              const currency =
                (window.shopSettings && window.shopSettings.isoCode) ||
                (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
                '';
              const currentPageName = document.title || '';
              const cartItems = Array.isArray(parsedState.items) ? parsedState.items : [];
              const matched =
                cartItems.find((ci) => String(ci.id) === String(variantId)) ||
                (parsedState && String(parsedState.id) === String(variantId) ? parsedState : null);
              const productId = matched && (matched.product_id || matched.productId) ? Number(matched.product_id || matched.productId) : null;
              const productName =
                (matched && (matched.product_title || matched.productTitle || matched.title) && String(matched.product_title || matched.productTitle || matched.title)) ||
                '';
              const priceCents = matched ? Number(matched.final_price ?? matched.price ?? 0) : 0;
              const displayPrice = Number.isFinite(priceCents) ? priceCents / 100 : 0;
              if (productId && productName) {
                sensors.track('AddToCart', {
                  product_id: String(productId),
                  variant_id: String(variantId),
                  product_name: productName,
                  display_price: displayPrice,
                  curency: currency,
                  current_page_name: currentPageName,
                  ysku_quantity: 1,
                });
              }
            }
          } catch (e) {
            // ignore
          }
          renderMiniCartWhenReady(parsedState, true);
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.classList.remove('loading');
          this.removeAttribute('disabled');
        });
    }
  }
}
customElements.define('add-to-cart', AddToCart);

class PriceMoney extends HTMLElement {
  constructor() {
    super();

    if (this.shouldInit()) {
      this.init();
    }
  }

  shouldInit() {
    const moneyFormat = shopSettings.moneyFormat.toLowerCase();
    if (moneyFormat.indexOf('class=') !== -1) {
      return false;
    }

    return true;
  }

  init() {
    const currencies_using_comma_decimals =
      'ANG,ARS,BRL,BYN,BYR,CLF,CLP,COP,CRC,CZK,DKK,EUR,HRK,HUF,IDR,ISK,MZN,NOK,PLN,RON,RUB,SEK,TRY,UYU,VES,VND'.split(',');
    const symbol = shopSettings.moneyFormat.replace(/\{{.*}}/, '').trim();
    const bdi = this.querySelector('bdi');
    const price = bdi.textContent;
    let html = price.replace(' ', '');
    let money_symbol_decimal = '.';

    if (currencies_using_comma_decimals.includes(shopSettings.isoCode)) {
      money_symbol_decimal = ',';
    }

    if (price.includes(money_symbol_decimal)) {
      if (price.lastIndexOf(symbol) + symbol.length === price.length) {
        const price_without_symbol = price.slice(0, price.lastIndexOf(symbol));
        const price_without_decimal = price.slice(0, price.lastIndexOf(money_symbol_decimal));
        const decimal = price_without_symbol.replace(price_without_decimal, '').trim();
        html = html.replace(decimal, `<sup class="price__suffix">${decimal}</sup>`);
      } else {
        const price_without_decimal = price.slice(0, price.lastIndexOf(money_symbol_decimal));
        const decimal = price.replace(price_without_decimal, '').trim();
        html = html.replace(decimal, `<sup class="price__suffix">${decimal}</sup>`);
      }
    }

    html = html.replace(symbol, `<span class="price__prefix">${symbol}</span>`);

    bdi.innerHTML = html;
  }
}
customElements.define('price-money', PriceMoney);

class SlideshowComponent extends HTMLElement {
  constructor() {
    super();

    this.mql = window.matchMedia('(min-width: 750px)');

    this.elements = {
      small: this.querySelector('.slideshow__left'),
      content: this.querySelector('.slideshow__content'),
      large: this.querySelector('.slideshow__right'),
    };
    this.settings = {
      autorotate: this.dataset.autorotate == 'true',
      autorotateSpeed: parseInt(this.dataset.autorotateSpeed),
      slidesSmall: this.elements.small.querySelectorAll('.slideshow__image'),
      slidesLarge: this.elements.large.querySelectorAll('.slideshow__image'),
    };

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 600,
    });
    this.matchMedia();
  }

  init() {
    setTimeout(() => {
      this.flickitySmall = new Flickity(this.elements.small, {
        accessibility: false,
        rightToLeft: theme.config.rtl,
        prevNextButtons: false,
        pageDots: false,
        wrapAround: true,
        draggable: false,
        setGallerySize: false,
        autoPlay: this.settings.autorotate ? this.settings.autorotateSpeed : false,
      });

      this.flickityContent = new Flickity(this.elements.content, {
        accessibility: false,
        rightToLeft: theme.config.rtl,
        prevNextButtons: true,
        pageDots: true,
        wrapAround: true,
        draggable: this.mql.matches ? false : true,
        asNavFor: this.elements.small,
        adaptiveHeight: true,
        arrowShape:
          'M14.453 30.078q-0.391 1.172-3.125 16.602t-2.539 15.625 16.406 3.125 16.406 2.734 0.195-2.148l0.391-1.563-13.281-2.344q-13.281-2.344-13.281-2.734t2.734-2.344 5.078-3.125q3.906-2.734 9.18-4.688t10.352-2.734q3.516-0.391 8.789-0.391t8.789 0.391q15.625 2.344 28.125 13.281l3.125 2.344 1.563-1.172q1.172-1.172 1.172-1.563t-3.32-3.125-5.273-4.297q-5.078-3.125-11.133-5.664t-11.914-3.711q-3.906-0.781-10.938-0.781t-10.938 0.781q-6.25 1.172-13.281 4.297t-11.719 6.641q-1.563 1.563-1.758 1.367t1.758-12.305l2.344-12.109q0-0.391-1.953-0.781h-1.563q-0.391 0-0.391 0.391z',
        on: {
          ready: () => {
            const prevNextButtons = this.querySelectorAll('.flickity-button');
            if (prevNextButtons) {
              prevNextButtons.forEach((button) => {
                button.setAttribute('tabindex', '-1');
              });
            }
          },
        },
      });

      this.flickityLarge = new Flickity(this.elements.large, {
        accessibility: false,
        rightToLeft: theme.config.rtl,
        prevNextButtons: false,
        pageDots: false,
        wrapAround: true,
        draggable: true,
        setGallerySize: false,
        asNavFor: this.elements.content,
      });

      this.bindEvents();
    });
  }

  bindEvents() {
    this.flickitySmall.on('scroll', () => {
      this.flickitySmall.slides.forEach((slide, i) => {
        const flickity = this.flickitySmall;
        const image = this.settings.slidesSmall[i];
        let x = 0;

        if (image) {
          if (0 === i) {
            x =
              Math.abs(flickity.x) > flickity.slidesWidth
                ? flickity.slidesWidth + flickity.x + flickity.slides[flickity.slides.length - 1].outerWidth + slide.target
                : slide.target + flickity.x;
          } else if (i === flickity.slides.length - 1) {
            x =
              Math.abs(flickity.x) + flickity.slides[i].outerWidth < flickity.slidesWidth
                ? slide.target - flickity.slidesWidth + flickity.x - flickity.slides[i].outerWidth
                : slide.target + flickity.x;
          } else {
            x = slide.target + flickity.x;
          }

          if (!theme.config.isTouch && !theme.config.rtl) {
            image.style.transform = 'translateX(' + x * (-1 / 2) + 'px)';
          }
        }
      });
    });

    this.flickityLarge.on('scroll', () => {
      this.flickityLarge.slides.forEach((slide, i) => {
        const flickity = this.flickityLarge;
        const image = this.settings.slidesLarge[i];
        let x = 0;

        if (image) {
          if (0 === i) {
            x =
              Math.abs(flickity.x) > flickity.slidesWidth
                ? flickity.slidesWidth + flickity.x + flickity.slides[flickity.slides.length - 1].outerWidth + slide.target
                : slide.target + flickity.x;
          } else if (i === flickity.slides.length - 1) {
            x =
              Math.abs(flickity.x) + flickity.slides[i].outerWidth < flickity.slidesWidth
                ? slide.target - flickity.slidesWidth + flickity.x - flickity.slides[i].outerWidth
                : slide.target + flickity.x;
          } else {
            x = slide.target + flickity.x;
          }

          if (!theme.config.isTouch && !theme.config.rtl) {
            image.style.transform = 'translateX(' + x * (-1 / 2) + 'px)';
          }
        }
      });
    });

    this.elements.content.querySelector('.flickity-button.next').addEventListener('click', () => {
      this.flickitySmall.next();
      this.stopPlayer();
    });

    this.elements.content.querySelector('.flickity-button.previous').addEventListener('click', () => {
      this.flickitySmall.previous();
      this.stopPlayer();
    });

    let currentIndex = null,
      swipeDirection = null;

    // Detect swipe direction
    this.flickityLarge.on('dragMove', (_event, _pointer, moveVector) => {
      currentIndex = this.flickityLarge.selectedIndex;
      swipeDirection = this.getSwipeDirection(moveVector);

      this.stopPlayer();
    });

    // Do stuff based on a successful swipe and it's direction
    this.flickityLarge.on('dragEnd', () => {
      if (this.flickityLarge.selectedIndex !== currentIndex) {
        if (swipeDirection === 'left') {
          this.flickitySmall.next();
        } else {
          this.flickitySmall.previous();
        }
      }
    });
  }

  stopPlayer() {
    if (this.settings.autorotate) {
      this.flickitySmall.stopPlayer();
    }
  }

  getSwipeDirection(moveVector) {
    return moveVector.x > 0 ? 'right' : 'left';
  }

  matchMedia() {
    document.addEventListener('matchSmall', () => {
      this.unload();
      this.init();
    });

    document.addEventListener('unmatchSmall', () => {
      this.unload();
      this.init();
    });
  }

  unload() {
    if (this.flickitySmall && typeof this.flickitySmall.destroy === 'function') {
      this.flickitySmall.destroy();
    }
    if (this.flickityContent && typeof this.flickityContent.destroy === 'function') {
      this.flickityContent.destroy();
    }
    if (this.flickityLarge && typeof this.flickityLarge.destroy === 'function') {
      this.flickityLarge.destroy();
    }
  }
}
customElements.define('slideshow-component', SlideshowComponent);

class TestimonialsComponent extends HTMLElement {
  constructor() {
    super();

    if (this.dataset.slider == 'true') {
      this.settings = {
        autorotate: this.dataset.autorotate == 'true',
        autorotateSpeed: parseInt(this.dataset.autorotateSpeed),
      };
      this.slider = this.querySelector('.testimonial__list');
      this.previews = this.querySelector('.testimonial__previews');

      theme.initWhenVisible({
        element: this,
        callback: this.init.bind(this),
        threshold: 600,
      });
      theme.initWhenVisible({
        element: this,
        callback: this.update.bind(this),
        threshold: 0,
      });
    }
  }

  init() {
    setTimeout(() => {
      this.flickity = new Flickity(this.slider, {
        accessibility: false,
        rightToLeft: theme.config.rtl,
        prevNextButtons: true,
        arrowShape: { x0: 10, x1: 60, y1: 50, x2: 65, y2: 45, x3: 20 },
        pageDots: false,
        wrapAround: true,
        draggable: true,
        cellAlign: 'center',
        autoPlay: this.settings.autorotate ? this.settings.autorotateSpeed : false,
        pauseAutoPlayOnHover: true,
        asNavFor: this.previews,
      });
      this.flickityNav = new Flickity(this.previews, {
        accessibility: false,
        rightToLeft: theme.config.rtl,
        prevNextButtons: false,
        pageDots: false,
        wrapAround: true,
        selectedAttraction: 0.2,
        friction: 0.8,
        adaptiveHeight: true,
      });

      this.flickityNav.previous();
      if (this.settings.autorotate) {
        this.flickity.pausePlayer();
      }

      this.flickity.on('staticClick', (_event, _pointer, _cellElement, cellIndex) => {
        if (typeof cellIndex == 'number') {
          this.flickityNav.select(cellIndex);
        }
      });

      // Do stuff based on a successful swipe and it's direction
      this.flickity.on('change', (index) => {
        this.flickityNav.select(index);
      });
    });
  }

  update() {
    setTimeout(() => {
      if (this.flickity && this.flickityNav) {
        this.flickityNav.next();
        if (this.settings.autorotate) {
          this.flickity.unpausePlayer();
        }
      }
    }, 300);
  }

  getSwipeDirection(moveVector) {
    return moveVector.x > 0 ? 'right' : 'left';
  }
}
customElements.define('testimonials-component', TestimonialsComponent);

class UseAnimate extends HTMLElement {
  constructor() {
    super();

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 0,
    });
  }

  init() {
    this.setAttribute('animate', '');
  }
}
customElements.define('use-animate', UseAnimate);

class AnimateSticky extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.onScrollHandler = this.onScroll.bind(this);

    window.addEventListener('scroll', this.onScrollHandler, false);
    this.onScrollHandler();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScrollHandler);
  }

  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (scrollTop >= this.getOffsetTop(this)) {
      requestAnimationFrame(this.reveal.bind(this));
    } else {
      requestAnimationFrame(this.reset.bind(this));
    }
  }

  reveal() {
    this.setAttribute('animate', '');
  }

  reset() {
    this.removeAttribute('animate');
  }

  getOffsetTop(element) {
    let offsetTop = 0;
    while (element) {
      offsetTop += element.offsetTop;
      element = element.offsetParent;
    }
    return offsetTop;
  }
}
customElements.define('animate-sticky', AnimateSticky);

class LookbookComponent extends HTMLElement {
  constructor() {
    super();

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 0,
    });
  }

  init() {
    const hotspots = this.querySelectorAll('.look__hotspot');
    hotspots.forEach((hotspot) => {
      hotspot.classList.add('active');
    });

    setTimeout(() => {
      hotspots.forEach((hotspot) => {
        hotspot.classList.remove('active');
      });
    }, 3e3);
  }
}
customElements.define('lookbook-component', LookbookComponent);

class TabCollage extends HTMLElement {
  constructor() {
    super();

    this.querySelectorAll('.tab-collage__heading').forEach((button) => button.addEventListener('click', this.onButtonClick.bind(this)));

    if (this.dataset.accordion === undefined) {
      this.querySelectorAll('.tab-collage__heading').forEach((button) =>
        button.addEventListener('mouseover', this.onButtonClick.bind(this))
      );
    }
  }

  onButtonClick(event) {
    const target = event.target;
    if (target.classList.contains('is-active')) {
      if (this.dataset.accordion !== undefined) {
        target.classList.remove('is-active');
      }
      return;
    }

    this.pauseAllMedia();

    this.querySelectorAll('[data-block-id]').forEach((button) => {
      if (target.dataset.blockId === button.dataset.blockId) {
        button.classList.add('is-active');
      } else {
        button.classList.remove('is-active');
      }
    });
  }

  pauseAllMedia() {
    this.querySelectorAll('video-section[data-type="youtube"] iframe').forEach((video) => {
      video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
    });
    this.querySelectorAll('video-section[data-type="vimeo"] iframe').forEach((video) => {
      video.contentWindow.postMessage('{"method":"pause"}', '*');
    });
  }
}
customElements.define('tab-collage', TabCollage);

class CountdownTimer extends HTMLElement {
  constructor() {
    super();

    this.parent = this.closest('.product-countdown');
    this.date = new Date(Date.parse(this.dataset.date)).getTime();

    if (isNaN(this.date)) {
      this.date = new Date(this.dataset.date.replace(/-/g, '/')).getTime();

      if (isNaN(this.date)) {
        this.unload();
        return;
      }
    }

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 200,
    });
  }

  init() {
    this.timer();
    this.dataset.interval = setInterval(this.timer.bind(this), 1000);
  }

  timer() {
    const now = new Date(),
      countTo = new Date(this.date),
      timeDifference = countTo - now;

    if (timeDifference < 0) {
      this.unload();
      return;
    }

    const secondsInADay = 60 * 60 * 1000 * 24,
      secondsInAHour = 60 * 60 * 1000;

    const days = Math.floor((timeDifference / secondsInADay) * 1);
    const hours = Math.floor(((timeDifference % secondsInADay) / secondsInAHour) * 1);
    const mins = Math.floor((((timeDifference % secondsInADay) % secondsInAHour) / (60 * 1000)) * 1);
    const secs = Math.floor(((((timeDifference % secondsInADay) % secondsInAHour) % (60 * 1000)) / 1000) * 1);

    if (this.dataset.compact == 'true') {
      const dayHTML = days > 0 ? `<div class="countdown__item"><span>${days}${dateStrings.d}</span></div>` : '';
      const otherHTML = `<div class="countdown__item"><span>${hours > 9 ? hours : '0' + hours}:${mins > 9 ? mins : '0' + mins}:${secs > 9 ? secs : '0' + secs
        }</span></div>`;

      this.innerHTML = dayHTML + otherHTML;
    } else {
      const dayHTML =
        days > 0 ? `<div class="countdown__item"><span>${days}</span> ${days == 1 ? dateStrings.day : dateStrings.days}</div>` : '';
      const hourHTML = `<div class="countdown__item"><span>${hours}</span> ${hours == 1 ? dateStrings.hour : dateStrings.hours}</div>`;
      const minHTML = `<div class="countdown__item"><span>${mins}</span> ${mins == 1 ? dateStrings.minute : dateStrings.minutes}</div>`;
      const secHTML = `<div class="countdown__item"><span>${secs}</span> ${secs == 1 ? dateStrings.second : dateStrings.seconds}</div>`;

      this.innerHTML = dayHTML + hourHTML + minHTML + secHTML;
    }
  }

  unload() {
    if (this.dataset.interval) {
      clearInterval(this.dataset.interval);
    }

    this.classList.add('hidden');
    if (this.parent) {
      this.parent.classList.add('hidden');
    }
  }
}
customElements.define('countdown-timer', CountdownTimer);

class GMap extends HTMLElement {
  constructor() {
    super();

    if (!this.dataset.apiKey || !this.dataset.mapAddress) {
      return;
    }

    theme.initWhenVisible({
      element: this,
      callback: this.prepMapApi.bind(this),
      threshold: 200,
    });
  }

  prepMapApi() {
    this.loadScript().then(this.initMap.bind(this));
  }

  loadScript() {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      document.body.appendChild(script);
      script.onload = resolve;
      script.onerror = reject;
      script.async = true;
      script.src = 'https://maps.googleapis.com/maps/api/js?key=' + this.dataset.apiKey;
    });
  }

  initMap() {
    const geocoder = new google.maps.Geocoder();

    geocoder.geocode({ address: this.dataset.mapAddress }, (results, status) => {
      if (status !== google.maps.GeocoderStatus.OK) {
        if (Shopify.designMode) {
        }
      } else {
        const mapOptions = {
          zoom: parseInt(this.dataset.zoom),
          center: results[0].geometry.location,
          draggable: true,
          clickableIcons: false,
          scrollwheel: false,
          disableDoubleClickZoom: true,
          disableDefaultUI: true,
        };

        const map = new google.maps.Map(this, mapOptions),
          center = map.getCenter();

        map.setCenter(center);

        const icon = {
          path: 'M32.7374478,5.617 C29.1154478,1.995 24.2994478,0 19.1774478,0 C14.0544478,0 9.23944778,1.995 5.61744778,5.617 C-1.08555222,12.319 -1.91855222,24.929 3.81344778,32.569 L19.1774478,54.757 L34.5184478,32.6 C40.2734478,24.929 39.4404478,12.319 32.7374478,5.617 Z M19.3544478,26 C15.4954478,26 12.3544478,22.859 12.3544478,19 C12.3544478,15.141 15.4954478,12 19.3544478,12 C23.2134478,12 26.3544478,15.141 26.3544478,19 C26.3544478,22.859 23.2134478,26 19.3544478,26 Z',
          fillColor: this.dataset.markerColor,
          fillOpacity: 1,
          anchor: new google.maps.Point(15, 55),
          strokeWeight: 0,
          scale: 0.6,
        };

        new google.maps.Marker({
          map: map,
          position: map.getCenter(),
          icon: icon,
        });

        const styledMapType = new google.maps.StyledMapType(JSON.parse(this.parentNode.querySelector('[data-gmap-style]').innerHTML));

        map.mapTypes.set('styled_map', styledMapType);
        map.setMapTypeId('styled_map');

        google.maps.event.addDomListener(window, 'resize', function () {
          google.maps.event.trigger(map, 'resize');
          map.setCenter(center);
        });
      }
    });
  }
}
customElements.define('g-map', GMap);

class RelatedButtons extends HTMLElement {
  constructor() {
    super();

    window.addEventListener('scroll', this.checkListener.bind(this));
    this.querySelectorAll('a').forEach((button) => button.addEventListener('click', this.onButtonClick.bind(this)));
  }

  checkListener() {
    const element = document.querySelector(this.dataset.scrollto);
    const show = window.scrollY >= element.offsetTop - element.clientHeight;

    if (show) {
      this.classList.add('is-flipped');
    } else {
      this.classList.remove('is-flipped');
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    const target = event.target;
    const scrollIntoView = document.querySelector(target.getAttribute('href'));

    scrollIntoView.scrollIntoView({ behavior: 'smooth' });
  }
}
customElements.define('related-buttons', RelatedButtons);

class ProductRecentlyViewed extends HTMLElement {
  constructor() {
    super();

    // We save the product ID in local storage to be eventually used for recently viewed section
    if (isStorageSupported('local')) {
      const productId = parseInt(this.dataset.productId);
      const productTitle = this.dataset.productTitle;
      const cookieName = 'beyours:recently-viewed';
      const items = JSON.parse(window.localStorage.getItem(cookieName) || '[]');

      // We check if the current product already exists, and if it does not, we add it at the start
      if (!items.includes(productId) && !productTitle.includes('Free Gift')) {
        items.unshift(productId);
      }

      // By keeping only the 4 most recent
      window.localStorage.setItem(cookieName, JSON.stringify(items.slice(0, 5)));
    }
  }
}
customElements.define('product-recently-viewed', ProductRecentlyViewed);

class RecentlyViewedProducts extends HTMLElement {
  constructor() {
    super();

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 600,
    });
  }

  init() {
    fetch(this.dataset.url + this.getQueryString())
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('recently-viewed-products');
        if (recommendations && recommendations.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }

  getQueryString() {
    const cookieName = 'beyours:recently-viewed';
    const items = JSON.parse(window.localStorage.getItem(cookieName) || '[]');
    if (this.dataset.productId && items.includes(parseInt(this.dataset.productId))) {
      items.splice(items.indexOf(parseInt(this.dataset.productId)), 1);
    }
    return items
      .map((item) => 'id:' + item)
      .slice(0, 4)
      .join(' OR ');
  }
}
customElements.define('recently-viewed-products', RecentlyViewedProducts);
// 视频出现在视口播放的intersection实例
const lazyVideoPlayObserver = new IntersectionObserver((videoList) => {
  videoList.forEach((video) => {
    const isInnerModalVideo = video.target.classList.contains('modal-video');
    if (video.isIntersecting && !isInnerModalVideo) {
      video.target.play();
    } else {
      video.target.pause();
    }
  });
});
class VideoSection extends HTMLElement {
  constructor() {
    super();

    this.background = this.dataset.initMode !== 'template';

    if (this.background) {
      theme.initWhenVisible({
        element: this,
        callback: this.init.bind(this),
        threshold: 600,
      });
    } else {
      this.init();
    }
  }

  init() {
    this.parentSelector = this.dataset.parent || '.media-wrapper';
    this.parent = this.closest(this.parentSelector);

    this.isMobile = window.innerWidth < 750;

    switch (this.dataset.type) {
      case 'youtube':
        this.initYoutubeVideo();
        break;

      case 'vimeo':
        this.initVimeoVideo();
        break;

      case 'mp4':
        this.initMp4Video();
        break;
    }
  }

  initYoutubeVideo() {
    this.setAsLoading();
    this.loadScript('youtube').then(this.setupYoutubePlayer.bind(this));
  }

  initVimeoVideo() {
    this.setAsLoading();
    this.loadScript('vimeo').then(this.setupVimeoPlayer.bind(this));
  }

  initMp4Video() {
    const player = this.querySelector('video');
    const videoId = this.judgeTruthVideoPath();

    player.src = videoId;
    if (player) {
      if (this.dataset.auto == '1') {
        // const promise = player.play();
        lazyVideoPlayObserver.observe(player);
      } else {
        player.setAttribute('controls', '');
      }

      // Edge does not return a promise (video still plays)
      if (typeof promise !== 'undefined') {
        promise
          .then(function () {
            // playback normal
          })
          .catch(function () {
            player.setAttribute('controls', '');
          });
      }
    }
  }

  judgeTruthVideoPath() {
    let video_url = this.isMobile ? this.dataset.mobileVideoId : this.dataset.videoId;
    // 取另一方的值，作为兜底
    if (!video_url) {
      video_url = this.isMobile ? this.dataset.videoId : his.dataset.mobileVideoId;
    }
    return video_url;
  }

  loadScript(videoType) {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      document.body.appendChild(script);
      script.onload = resolve;
      script.onerror = reject;
      script.async = true;
      script.src = videoType === 'youtube' ? '//www.youtube.com/iframe_api' : '//player.vimeo.com/api/player.js';
    });
  }

  setAsLoading() {
    this.parent.setAttribute('loading', true);
  }

  setAsLoaded() {
    this.parent.removeAttribute('loading');
    this.parent.setAttribute('loaded', true);
  }

  setupYoutubePlayer() {
    const videoId = this.judgeTruthVideoPath();

    const playerInterval = setInterval(() => {
      if (window.YT) {
        window.YT.ready(() => {
          if (!this.querySelector('iframe')) {
            const element = document.createElement('div');
            this.appendChild(element);

            this.player = new YT.Player(element, {
              videoId: videoId,
              playerVars: {
                autoplay: 0,
                showinfo: 0,
                controls: !this.background,
                fs: !this.background,
                rel: 0,
                height: '100%',
                width: '100%',
                iv_load_policy: 3,
                html5: 1,
                loop: 0,
                playsinline: 1,
                modestbranding: 1,
                disablekb: 1,
              },
              events: {
                onReady: this.onYoutubeReady.bind(this),
                onStateChange: this.onYoutubeStateChange.bind(this),
              },
            });
            clearInterval(playerInterval);
          }
        });
      }
    }, 50);
  }

  onYoutubeReady() {
    this.iframe = this.querySelector('iframe'); // iframe once YT loads
    this.iframe.setAttribute('tabindex', '-1');

    this.background && typeof this.player.mute === 'function' && this.player.mute();
    if (typeof this.player.playVideo === 'function' && this.dataset.auto == '1') this.player.playVideo();

    this.setAsLoaded();

    // pause when out of view
    var observer = new IntersectionObserver(
      (entries, _observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (this.dataset.auto == '1') {
              this.youtubePlay();
            }
          } else {
            this.youtubePause();
          }
        });
      },
      { rootMargin: '0px 0px 50px 0px' }
    );

    observer.observe(this.iframe);
  }

  onYoutubeStateChange(event) {
    switch (event.data) {
      case -1: // unstarted
        // Handle low power state on iOS by checking if
        // video is reset to unplayed after attempting to buffer
        if (this.attemptedToPlay) {
          this.setAsLoaded();
          this.closest('.banner').classList.add('video-interactable');
        }
        break;
      case 0: // ended, loop it
        this.youtubePlay();
        break;
      case 1: // playing
        this.setAsLoaded();
        break;
      case 3: // buffering
        this.attemptedToPlay = true;
        break;
    }
  }

  youtubePlay() {
    if (this.background && this.player && typeof this.player.playVideo === 'function') {
      this.player.playVideo();
    }
  }

  youtubePause() {
    if (this.background && this.player && typeof this.player.pauseVideo === 'function') {
      this.player.pauseVideo();
    }
  }

  setupVimeoPlayer() {
    const videoId = this.judgeTruthVideoPath();

    const playerInterval = setInterval(() => {
      if (window.Vimeo) {
        this.player = new Vimeo.Player(this, {
          id: videoId,
          autoplay: true,
          autopause: false,
          background: this.background,
          controls: !this.background,
          loop: true,
          height: '100%',
          width: '100%',
        });
        this.player.ready().then(this.onVimeoReady.bind(this));

        clearInterval(playerInterval);
      }
    }, 50);
  }

  onVimeoReady() {
    this.iframe = this.querySelector('iframe');
    this.iframe.setAttribute('tabindex', '-1');

    this.background && this.player.setMuted(true);

    this.setAsLoaded();

    // pause when out of view
    var observer = new IntersectionObserver(
      (entries, _observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.vimeoPlay();
          } else {
            this.vimeoPause();
          }
        });
      },
      { rootMargin: '0px 0px 50px 0px' }
    );

    observer.observe(this.iframe);
  }

  vimeoPlay() {
    if (this.background && this.player && typeof this.player.play === 'function') {
      this.player.play();
    }
  }

  vimeoPause() {
    if (this.background && this.player && typeof this.player.pause === 'function') {
      this.player.pause();
    }
  }
}
customElements.define('video-section', VideoSection);

class BundleProduct extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      souldout: 'price--sold-out',
      onsale: 'price--on-sale',
      // nocompare: 'price--no-compare',
    };

    this.price = this.querySelector('.price');
    this.variants = this.querySelector('select');
    if (this.variants) {
      this.variants.addEventListener('change', this.onSelectChange.bind(this));
    }
  }

  onSelectChange(event) {
    const variants = event.target;
    const variant = variants.options[variants.selectedIndex];
    const new_img_src = '/' + variant.dataset.img_src + '?width=140';
    const compare_at_price = parseInt(variant.dataset.compare_at_price || 0);
    const price = parseInt(variant.dataset.price || 0);
    const available = variant.dataset.available === 'true';
    const price_varies = variants.dataset.price_varies === 'true';
    const compare_at_price_varies = variants.dataset.compare_at_price_varies === 'true';

    // Remove classes
    this.price.classList.remove(this.classes.souldout);
    this.price.classList.remove(this.classes.onsale);
    this.price.classList.remove(this.classes.nocompare);

    // Add classes
    if (available === false) {
      this.price.classList.add(this.classes.souldout);
    } else if (compare_at_price > price && available) {
      this.price.classList.add(this.classes.onsale);
    }
    // if (price_varies == false && compare_at_price_varies) {
    //   this.price.classList.add(this.classes.nocompare);
    // }

    // Change price
    const price__regular = this.querySelector('.price__regular');
    price__regular.querySelector('.price-item--regular').innerHTML = `<price-money><bdi>${theme.Currency.formatMoney(
      price,
      shopSettings.moneyFormat
    )}</bdi></price-money>`;

    const price__sale = this.querySelector('.price__sale');
    price__sale.querySelector('.price-item--regular').innerHTML = `<price-money><bdi>${theme.Currency.formatMoney(
      compare_at_price,
      shopSettings.moneyFormat
    )}</bdi></price-money>`;
    price__sale.querySelector('.price-item--sale').innerHTML = `<price-money><bdi>${theme.Currency.formatMoney(
      price,
      shopSettings.moneyFormat
    )}</bdi></price-money>`;

    // Change img
    const regex = /\/products\/\S*$/g;
    this.querySelector('img').srcset = '';
    this.querySelector('img').src = this.querySelector('img').src.replace(regex, new_img_src);
  }
}
customElements.define('bundle-product', BundleProduct);

class BundleProducts extends HTMLElement {
  constructor() {
    super();

    this.button = this.querySelector('button');
    if (this.button) this.button.addEventListener('click', this.onButtonClick.bind(this));

    this.blocks = this.querySelectorAll('[data-block-id]');
    this.blocks.forEach((block) => block.addEventListener('mouseenter', this.onEnterHandler.bind(this)));
    this.blocks.forEach((block) => block.addEventListener('mouseleave', this.onLeaveHandler.bind(this)));
  }

  get miniCart() {
    return getReadyMiniCart();
  }

  onEnterHandler(event) {
    this.classList.add('hovering');

    const target = event.target;
    const blockId = target.dataset.blockId;
    this.blocks.forEach((block) => {
      if (target.nodeName === 'BUNDLE-PRODUCT') {
        if (block.nodeName === 'BUNDLE-PRODUCT') {
          block.classList.add('active');
          return;
        }
      }
      if (block.dataset.blockId === blockId) {
        block.classList.add('active');
      }
    });
  }

  onLeaveHandler() {
    this.classList.remove('hovering');
    this.blocks.forEach((block) => block.classList.remove('active'));
  }

  onButtonClick(event) {
    event.preventDefault();

    const ids = this.querySelectorAll('[name="id"]');
    const items = {
      items: [...ids].map((e) => {
        return {
          id: e.value,
          quantity: 1,
          properties: {
            combineConfig: {
              combineId: generateId(),
              combineType: 'product',
              itemType: 'accessories',
            },
            version: window.commentPropertiesVersion,
            productId: +e.dataset.id,
            actualProductId: +e.dataset.id,
            skuId: +e.value,
            productCombineId: generateId(),
          },
        };
      }),
    };

    if (document.body.classList.contains('template-cart') || !shopSettings.cartDrawer) {
      Shopify.postLink2(routes.cart_add_url, {
        parameters: {
          ...items,
        },
      });
      return;
    }

    this.handleErrorMessage();

    this.button.setAttribute('disabled', true);
    this.button.classList.add('loading');
    const sections = this.miniCart
      ? this.miniCart.getSectionsToRender().map((section) => section.section)
      : ['mini-cart', 'cart-icon-bubble'];

    const body = JSON.stringify({
      ...items,
      sections: sections,
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_add_url}`, { ...fetchConfig('javascript'), body })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.handleErrorMessage(response.description);
          return;
        }

        // Sensors: AddToCart（bundle 多商品，需要拆分逐条上报）
        try {
          const sensors = window.sensorsDataAnalytic201505 || window['sensorsDataAnalytic201505'];
          if (sensors && typeof sensors.track === 'function') {
            const currency =
              (window.shopSettings && window.shopSettings.isoCode) ||
              (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
              '';
            const currentPageName = document.title || '';
            const cartItems = Array.isArray(response.items) ? response.items : [];

            (items.items || []).forEach((reqItem) => {
              const variantId = Number(reqItem.id);
              const quantity = Number(reqItem.quantity || 0);
              if (!variantId || !quantity) return;
              const matched = cartItems.find((ci) => Number(ci.id) === variantId);
              const productId =
                (matched && (matched.product_id || matched.productId) && Number(matched.product_id || matched.productId)) ||
                (reqItem.properties && (reqItem.properties.productId || reqItem.properties._productId) && Number(reqItem.properties.productId || reqItem.properties._productId)) ||
                null;
              const productName =
                (matched && (matched.product_title || matched.productTitle || matched.title) && String(matched.product_title || matched.productTitle || matched.title)) ||
                '';
              const priceCents = matched ? Number(matched.final_price ?? matched.price ?? 0) : 0;
              const displayPrice = Number.isFinite(priceCents) ? priceCents / 100 : 0;
              if (!productId || !productName) return;
              sensors.track('AddToCart', {
                product_id: String(productId),
                variant_id: String(variantId),
                product_name: productName,
                display_price: displayPrice,
                curency: currency,
                current_page_name: currentPageName,
                ysku_quantity: quantity,
              });
            });
          }
        } catch (e) {
          // ignore
        }

        renderMiniCartWhenReady(response, true);
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.button.classList.remove('loading');
        this.button.removeAttribute('disabled');
      });
  }

  handleErrorMessage(errorMessage = false) {
    this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');

    if (this.errorMessageWrapper) {
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');
      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    } else {
      if (errorMessage) {
        alert(errorMessage);
      }
    }
  }
}
customElements.define('bundle-products', BundleProducts);

class ShopTheLook extends HTMLElement {
  constructor() {
    super();

    if (this.dataset.blockCount == 1) {
      return;
    }

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 600,
    });
  }

  init() {
    this.products = this.querySelector('.image-with-text__products');

    this.blocks = this.querySelectorAll('lookbook-component [data-block-index]');
    this.blocks.forEach((block) => block.addEventListener('mouseenter', this.onEnterHandler.bind(this)));

    this.products
      .querySelectorAll('.quick-view__summary')
      .forEach((button) => button.addEventListener('click', this.onButtonClick.bind(this)));

    this.initSlider();
  }

  onButtonClick(event) {
    event.preventDefault();

    const block = event.target.closest('.product-container');
    const button = this.querySelector(`lookbook-component [data-block-index="${block.dataset.blockIndex}"]`);
    const summaryElement = button.closest('summary');
    summaryElement && summaryElement.click();
  }

  onEnterHandler(event) {
    if (this.flickity && typeof this.flickity.select === 'function') {
      const index = parseInt(event.target.dataset.blockIndex);
      this.flickity.select(index);
    }
  }

  onFocusHandler(index) {
    this.blocks.forEach((block) => {
      block.classList.remove('focus');

      if (parseInt(block.dataset.blockIndex) === index) {
        block.classList.add('focus');
      }
    });
  }

  initSlider() {
    this.flickity = new Flickity(this.products, {
      accessibility: false,
      rightToLeft: theme.config.rtl,
      prevNextButtons: false,
      pageDots: true,
      wrapAround: true,
      fade: true,
      setGallerySize: true,
      on: {
        ready: () => {
          this.onFocusHandler(0);
        },
        change: (index) => {
          this.onFocusHandler(index);
        },
      },
    });
  }
}
customElements.define('shop-the-look', ShopTheLook);

class SelectWrapper extends HTMLElement {
  constructor() {
    super();

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 200,
    });

    this.select = this.querySelector('select');
    if (this.select) {
      this.select.addEventListener('change', this.handleSelectChange.bind(this));
    }
  }

  init() {
    const style = window.getComputedStyle(this.select);
    const value = this.select.options[this.select.selectedIndex].text;

    const text = document.createElement('span');
    text.style.fontFamily = style.fontFamily;
    text.style.fontSize = style.fontSize;
    text.style.fontWeight = style.fontWeight;
    text.style.visibility = 'hidden';
    text.style.position = 'absolute';
    text.innerHTML = value;

    document.body.appendChild(text);
    const width = text.clientWidth;

    this.style.setProperty('--width', `${width}px`);
    text.remove();
  }

  handleSelectChange() {
    this.init();
  }
}
customElements.define('select-wrapper', SelectWrapper);

class ImageComparison extends HTMLElement {
  constructor() {
    super();

    this.active = false;
    this.button = this.querySelector('button');
    this.horizontal = this.dataset.layout === 'horizontal';
    this.init();

    theme.initWhenVisible({
      element: this.querySelector('.image-comparison__animate'),
      callback: this.animate.bind(this),
      threshold: 0,
    });
  }

  animate() {
    this.setAttribute('animate', '');
    this.classList.add('animating');
    setTimeout(() => {
      this.classList.remove('animating');
    }, 1e3);
  }

  init() {
    if (theme.config.isTouch) {
      this.button.addEventListener('touchstart', this.startHandler.bind(this));
      document.body.addEventListener('touchend', this.endHandler.bind(this));
      document.body.addEventListener('touchmove', this.onHandler.bind(this));
    } else {
      this.button.addEventListener('mousedown', this.startHandler.bind(this));
      document.body.addEventListener('mouseup', this.endHandler.bind(this));
      document.body.addEventListener('mousemove', this.onHandler.bind(this));
    }
  }

  startHandler() {
    this.active = true;
    this.classList.add('scrolling');
  }

  endHandler() {
    this.active = false;
    this.classList.remove('scrolling');
  }

  onHandler(e) {
    if (!this.active) return;

    const event = (e.touches && e.touches[0]) || e;
    const x = this.horizontal ? event.pageX - this.offsetLeft : event.pageY - this.offsetTop;

    this.scrollIt(x);
  }

  scrollIt(x) {
    const distance = this.horizontal ? this.clientWidth : this.clientHeight;

    const max = distance - 20;
    const min = 20;
    const mouseX = Math.max(min, Math.min(x, max));
    const mousePercent = (mouseX * 100) / distance;
    this.style.setProperty('--percent', mousePercent + '%');
  }
}
customElements.define('image-comparison', ImageComparison);

function parseShopifyRichTextToHTML(content) {
  if (!content) return '';
  let html = '';
  content.children.forEach((node) => {
    switch (node.type) {
      case 'heading':
        html += `<h${node.level}>${node.children[0].value}</h${node.level}>`;
        break;
      case 'list':
        html += `<${node.listType === 'unordered' ? 'ul' : 'ol'}>`;
        node.children.forEach((item) => {
          html += `<li>${item.children[0].value}</li>`;
        });
        html += `<${node.listType === 'unordered' ? '/ul' : '/ol'}>`;
        break;
      case 'paragraph':
        html += `<p>`;
        node.children.forEach((item) => {
          if (item.type === 'text' && item.bold) {
            html += `<strong>${item.value}</strong>` + ' ';
          } else if (item.type === 'text' && item.italic) {
            html += `<em>${item.value}</em>` + ' ';
          } else if (item.type === 'text') {
            html += `${item.value}` + ' ';
          }
          if (item.type === 'link' && item.bold) {
            html += `<a href="${item.url}" target="${item.target}"><strong>${item.children[0].value}</strong></a>` + ' ';
          } else if (item.type === 'link' && item.italic) {
            html += `<a href="${item.url}" target="${item.target}"><em>${item.children[0].value}</em></a>` + ' ';
          } else if (item.type === 'link') {
            html += `<a href="${item.url}" target="${item.target}">${item.children[0].value}</a>` + ' ';
          }
        });
        html += `</p>`;
        break;
    }
  });
  return html;
}
